import React, { useState, useEffect, useRef } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Stack,
  MenuItem,
  IconButton,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorOutlineIcon,
} from '@mui/icons-material';
import axios from '../../config/axios';
import { API_ENDPOINTS } from '../../config/api';
import submitResultsService from '../../services/submitResults.service';
import PdfJsHighlightViewer from '../documentEditor/PdfJsHighlightViewer';

// Same locally-bundled worker as the other pdfjs-dist consumers (PdfJsSimpleViewer,
// PdfJsHighlightViewer, PdfViewersTab, ClinicalIntakeForm) — this previously loaded
// the worker from cdnjs.cloudflare.com keyed by pdfjsLib.version, an unpinned
// third-party script with no integrity check (security review SEC-003 territory:
// a compromised/mismatched CDN file would run inside the PDF worker).
// Not `new URL('pdfjs-dist/...', import.meta.url)` — react-scripts' Babel pass
// over bundled .mjs files injects absolute-filesystem-path helper imports
// that break once emitted as a standalone asset (see PdfJsHighlightViewer.jsx).
// scripts/copy-pdf-worker.js (postinstall) copies the worker into public/
// untouched instead.
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

const reasonNotCompletedOptions = [
  'Adverse Event',
  'Death',
  'Lack of Efficacy',
  'Lost to Follow-Up',
  'Physician Decision',
  'Pregnancy',
  'Protocol Violation',
  'Withdrawal by Subject',
  'Other',
];

// Baseline Measure Title Options
const baselineMeasureTitleOptions = [
  'Age',
  'Sex/Gender',
  'Race and Ethnicity',
  'Region of Enrollment',
  'Study-Specific Measure',
];

// Age sub-options
const ageSubOptions = [
  'Age, Continuous',
  'Age, Categorical',
  'Age, Customized',
];

// Age Categorical options
const ageCategoricalOptions = [
  '<=18 years',
  '>18 and <65 years',
  '>=65 years',
];

// Sex/Gender sub-options
const sexGenderSubOptions = [
  'Sex: Female, Male',
  'Sex/Gender, Customized',
];

const sexGenderFemaleMaleOptions = [
  'Female',
  'Male',
];

// Race and Ethnicity sub-options
const raceEthnicitySubOptions = [
  'Race (NIH/OMB)',
  'Ethnicity (NIH/OMB)',
  'Race/Ethnicity, Customized',
  'Race and Ethnicity Not Collected',
];

const raceNIHOMBOptions = [
  'American Indian or Alaska Native',
  'Asian',
  'Native Hawaiian or Pacific Islander',
  'Black or African American',
  'White',
  'More than one race',
  'Unknown or Not Reported',
];

const ethnicityNIHOMBOptions = [
  'Hispanic or Latino',
  'Not Hispanic or Latino',
  'Unknown or Not Reported',
];

// Outcome Measures constants
const outcomeMeasureTypeOptions = ['Primary', 'Secondary', 'Other Pre-specified', 'Post-Hoc'];

const measureTypeOptions = [
  'Count of Participants',
  'Mean',
  'Median',
  'Least Squares Mean',
  'Geometric Mean',
  'Geometric Least Squares Mean',
  'Number',
  'Count of Units',
];

const dispersionPrecisionOptions = [
  'Not Applicable',
  'Standard Deviation',
  'Standard Error',
  'Inter-Quartile Range',
  'Full Range',
  '80% Confidence Interval',
  '90% Confidence Interval',
  '95% Confidence Interval',
  '97.5% Confidence Interval',
  '99% Confidence Interval',
  'Other Confidence Interval Level',
  'Geometric Coefficient of Variation',
];

const statisticalTestTypeOptions = [
  'Superiority',
  'Non-inferiority',
  'Equivalence',
  'Other (for example, single group or other descriptive analysis)',
  'Non-Inferiority or Equivalence (legacy selection)',
  'Superiority or Other (legacy selection)',
];

const statisticalMethodOptions = [
  'ANCOVA',
  'ANOVA',
  'Chi-Squared',
  'Chi-Squared, Corrected',
  'Cochran-Mantel-Haenszel',
  'Fisher Exact',
  'Kruskal-Wallis',
  'Log Rank',
  'Mantel Haenszel',
  'McNemar',
  'Mixed Models Analysis',
  'Regression, Cox',
  'Regression, Linear',
  'Regression, Logistic',
  'Sign Test',
  't-Test, 1-Sided',
  't-Test, 2-Sided',
  'Wilcoxon (Mann-Whitney)',
  'Other',
];

const estimationParameterOptions = [
  'Cox Proportional Hazard',
  'Hazard Ratio (HR)',
  'Hazard Ratio, Log',
  'Mean Difference (Final Values)',
  'Mean Difference (Net)',
  'Median Difference (Final Values)',
  'Median Difference (Net)',
  'Odds Ratio (OR)',
  'Odds Ratio, Log',
  'Risk Difference (RD)',
  'Risk Ratio (RR)',
  'Risk Ratio, Log',
  'Slope',
  'Other',
];

// Adverse Events constants
const collectionApproachOptions = ['Systematic Assessment', 'Non-Systematic Assessment'];

const organSystemOptions = [
  'Blood and Lymphatic System Disorders',
  'Cardiac Disorders',
  'Congenital, Familial and Genetic Disorders',
  'Ear and Labyrinth Disorders',
  'Endocrine Disorders',
  'Eye Disorders',
  'Gastrointestinal Disorders',
  'General Disorders',
  'Hepatobiliary Disorders',
  'Immune System Disorders',
  'Infections and Infestations',
  'Injury, Poisoning and Procedural Complications',
  'Investigations',
  'Metabolism and Nutrition Disorders',
  'Musculoskeletal and Connective Tissue Disorders',
  'Neoplasms Benign, Malignant and Unspecified (Including Cysts and Polyps)',
  'Nervous System Disorders',
  'Pregnancy, Puerperium and Perinatal Conditions',
  'Product Issues',
  'Psychiatric Disorders',
  'Renal and Urinary Disorders',
  'Reproductive System and Breast Disorders',
  'Respiratory, Thoracic and Mediastinal Disorders',
  'Skin and Subcutaneous Tissue Disorders',
  'Social Circumstances',
  'Surgical and Medical Procedures',
  'Vascular Disorders',
];

const initialFormData = {
  recruitment_details: '',
  pre_assignment_details: '',
  arms_groups: [
    {
      title: '',
      description: '',
      type_of_units_assigned: '',
    },
  ],
  periods: [
    {
      title: '',
      milestones: [
        { title: 'Started', data: '', comments: '', is_required: true },
        { title: 'Completed', data: '', comments: '', is_required: true },
      ],
      not_completed: {
        number: '', // Calculated automatically
      },
      reasons_not_completed: [],
    },
  ],
  baseline_characteristics: {
    arms_groups: [
      {
        title: '',
        description: '',
        overall_baseline_participants: '', // Per arm/group
        overall_units_analyzed: '', // Per arm/group
      },
    ],
    analysis_population: {
      type_of_units_analyzed: '', // Shared
      analysis_population_description: '', // Shared
    },
    measures: [
      {
        measure_title: '', // Main selection: Age, Sex/Gender, Race and Ethnicity, etc.
        measure_sub_type: '', // Sub-selection: Age, Continuous, Sex: Female/Male, etc.
        study_specific_title: '', // Only shown if "Study-Specific Measure" is selected
        description: '',
        measure_type: '',
        dispersion_type: '',
        number_of_baseline_participants: '',
        number_of_units_analyzed: '',
        analysis_population_type: '',
        measure_analysis_population_description: '',
        unit_of_measure: '',
        rows: [ // Multiple rows/categories per measure (e.g., Age Categorical: <=18, >18&<65, >=65)
          {
            category_or_row_title: '',
            data_per_arm: [], // Array of { arm_group_index, baseline_measure_data } - one per arm/group
            na_explanation: '',
          },
        ],
      },
    ],
  },
  outcome_measures: [], // Start empty, user can add measures
  adverse_events: {
    time_frame: '',
    adverse_event_reporting_description: '',
    source_vocabulary_name_default: '',
    collection_approach_default: '',
    arms_groups: [
      {
        title: '',
        description: '',
      },
    ],
    all_cause_mortality: {
      data_per_arm: [], // [{ arm_group_index, total_affected, total_at_risk }]
    },
    serious_adverse_events: {
      data_per_arm: [], // [{ arm_group_index, total_affected, total_at_risk }]
      events: [
        {
          adverse_event_term: '',
          organ_system: '',
          additional_description: '',
          source_vocabulary_name: '',
          collection_approach: '',
          data_per_arm: [], // [{ arm_group_index, number_affected, number_at_risk, number_of_events }]
        },
      ],
    },
    other_adverse_events: {
      frequency_threshold: '',
      data_per_arm: [], // [{ arm_group_index, total_affected, total_at_risk }]
      events: [
        {
          adverse_event_term: '',
          organ_system: '',
          additional_description: '',
          source_vocabulary_name: '',
          collection_approach: '',
          data_per_arm: [], // [{ arm_group_index, number_affected, number_at_risk, number_of_events }]
        },
      ],
    },
  },
};

const SubmitResultsPage = ({ projectId }) => {
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [fileInfo, setFileInfo] = useState(null);
  const [extractionMetadata, setExtractionMetadata] = useState({});
  const pdfViewerRef = useRef(null);
  const [programmaticHighlights, setProgrammaticHighlights] = useState([]);
  
  // File validation state
  const [isValidatingFile, setIsValidatingFile] = useState(false);
  const [fileValidationStatus, setFileValidationStatus] = useState(null); // { isValid: boolean, reason: string }

  useEffect(() => {
    if (projectId) {
      // Load existing data if available
      loadFormData();
    }
  }, [projectId]);

  // Generate highlights when both fileInfo and extractionMetadata are available
  // Use a ref to track if we've already attempted to generate highlights
  const highlightsGeneratedRef = useRef(false);
  
  useEffect(() => {
    if (fileInfo?.azureUrl && Object.keys(extractionMetadata).length > 0 && !highlightsGeneratedRef.current) {
      highlightsGeneratedRef.current = true; // Mark as attempted
      
      // Try to load saved highlights first, then generate if needed
      const loadAndGenerateHighlights = async () => {
        try {
          const pdfId = `submit-results-${projectId}`;
          const highlightsRes = await fetch(`/api/pdf-highlights?pdfId=${encodeURIComponent(pdfId)}`);
          
          if (highlightsRes.ok && highlightsRes.status !== 405) {
            // Check if response is JSON before parsing
            const contentType = highlightsRes.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
              try {
                const highlightsData = await highlightsRes.json();
                const savedHighlights = highlightsData.highlights || [];
                const savedProgrammaticHighlights = savedHighlights.filter(h => h.isProgrammatic);
                if (savedProgrammaticHighlights.length > 0) {
                  console.log(`[SubmitResults] Loaded ${savedProgrammaticHighlights.length} saved programmatic highlights`);
                  setProgrammaticHighlights(savedProgrammaticHighlights);
                  return; // Don't generate if we loaded saved ones
                }
              } catch (jsonErr) {
                // Invalid JSON, continue to generate
              }
            }
          }
          
          // No saved highlights or API not available, generate from metadata
          convertMetadataToHighlights(extractionMetadata).catch(err => {
            console.error('[SubmitResults] Error converting metadata to highlights:', err);
          });
        } catch (loadErr) {
          // API not available or error, generate from metadata
          convertMetadataToHighlights(extractionMetadata).catch(err => {
            console.error('[SubmitResults] Error converting metadata to highlights:', err);
          });
        }
      };
      
      loadAndGenerateHighlights();
    }
  }, [fileInfo?.azureUrl, extractionMetadata, projectId]);
  
  // Reset the ref when projectId changes
  useEffect(() => {
    highlightsGeneratedRef.current = false;
  }, [projectId]);

  // ===== Outcome Measures Helper Functions =====
  const addOutcomeMeasure = () => {
    setFormData((prev) => ({
      ...prev,
      outcome_measures: [
        ...(prev.outcome_measures || []),
        {
          outcome_measure_type: '',
          outcome_measure_title: '',
          outcome_measure_description: '',
          outcome_measure_time_frame: '',
          anticipated_reporting_date: '',
          arms_groups: [{ title: '', description: '', overall_participants_analyzed: '', overall_units_analyzed: '' }],
          analysis_population: {
            type_of_units_analyzed: '', // Shared
            analysis_population_description: '', // Shared
          },
          measure_type: '',
          dispersion_precision: '',
          other_confidence_interval_level: '',
          unit_of_measure: '',
          rows: [{ category_or_row_title: '', data_per_arm: [], na_explanation: '' }], // data_per_arm: [{ arm_group_index, outcome_measure_data, number_of_participants_analyzed, number_of_units_analyzed }]
          statistical_analyses: [],
        },
      ],
    }));
  };

  const removeOutcomeMeasure = (index) => {
    setFormData((prev) => ({
      ...prev,
      outcome_measures: (prev.outcome_measures || []).filter((_, i) => i !== index),
    }));
  };

  const updateOutcomeMeasure = (index, field, value) => {
    setFormData((prev) => {
      const measures = [...(prev.outcome_measures || [])];
      if (field === 'analysis_population') {
        // Special handling for nested object
        measures[index] = { ...measures[index], analysis_population: value };
      } else {
        measures[index] = { ...measures[index], [field]: value };
      }
      return { ...prev, outcome_measures: measures };
    });
  };

  const addOutcomeMeasureArmGroup = (measureIndex) => {
    setFormData((prev) => {
      const measures = [...(prev.outcome_measures || [])];
      measures[measureIndex] = {
        ...measures[measureIndex],
        arms_groups: [...(measures[measureIndex].arms_groups || []), { title: '', description: '', overall_participants_analyzed: '', overall_units_analyzed: '' }],
      };
      return { ...prev, outcome_measures: measures };
    });
  };

  const removeOutcomeMeasureArmGroup = (measureIndex, armIndex) => {
    setFormData((prev) => {
      const measures = [...(prev.outcome_measures || [])];
      measures[measureIndex] = {
        ...measures[measureIndex],
        arms_groups: (measures[measureIndex].arms_groups || []).filter((_, i) => i !== armIndex),
      };
      return { ...prev, outcome_measures: measures };
    });
  };

  const updateOutcomeMeasureArmGroup = (measureIndex, armIndex, field, value) => {
    setFormData((prev) => {
      const measures = [...(prev.outcome_measures || [])];
      const arms = [...(measures[measureIndex].arms_groups || [])];
      arms[armIndex] = { ...arms[armIndex], [field]: value };
      measures[measureIndex] = { ...measures[measureIndex], arms_groups: arms };
      return { ...prev, outcome_measures: measures };
    });
  };

  const addOutcomeMeasureRow = (measureIndex) => {
    setFormData((prev) => {
      const measures = [...(prev.outcome_measures || [])];
      measures[measureIndex] = {
        ...measures[measureIndex],
        rows: [
          ...(measures[measureIndex].rows || []),
          { category_or_row_title: '', number_of_participants_analyzed: '', number_of_units_analyzed: '', outcome_measure_data: '', na_explanation: '' },
        ],
      };
      return { ...prev, outcome_measures: measures };
    });
  };

  const removeOutcomeMeasureRow = (measureIndex, rowIndex) => {
    setFormData((prev) => {
      const measures = [...(prev.outcome_measures || [])];
      measures[measureIndex] = {
        ...measures[measureIndex],
        rows: (measures[measureIndex].rows || []).filter((_, i) => i !== rowIndex),
      };
      return { ...prev, outcome_measures: measures };
    });
  };

  const updateOutcomeMeasureRow = (measureIndex, rowIndex, field, value) => {
    setFormData((prev) => {
      const measures = [...(prev.outcome_measures || [])];
      const rows = [...(measures[measureIndex].rows || [])];
      if (field === 'data_per_arm') {
        rows[rowIndex] = { ...rows[rowIndex], data_per_arm: value };
      } else {
        rows[rowIndex] = { ...rows[rowIndex], [field]: value };
      }
      measures[measureIndex] = { ...measures[measureIndex], rows };
      return { ...prev, outcome_measures: measures };
    });
  };

  // Helper to get data_per_arm entry for a specific arm in a row
  const getOutcomeRowDataForArm = (measureIndex, rowIndex, armIndex) => {
    const measure = formData.outcome_measures?.[measureIndex];
    const row = measure?.rows?.[rowIndex];
    const dataPerArm = row?.data_per_arm || [];
    return dataPerArm.find(d => d.arm_group_index === armIndex) || { arm_group_index: armIndex, outcome_measure_data: '', number_of_participants_analyzed: '', number_of_units_analyzed: '' };
  };

  // Helper to update data_per_arm entry for a specific arm in a row
  const updateOutcomeRowDataForArm = (measureIndex, rowIndex, armIndex, field, value) => {
    setFormData((prev) => {
      const measures = [...(prev.outcome_measures || [])];
      const rows = [...(measures[measureIndex].rows || [])];
      const dataPerArm = [...(rows[rowIndex]?.data_per_arm || [])];
      let armDataIndex = dataPerArm.findIndex(d => d.arm_group_index === armIndex);
      if (armDataIndex === -1) {
        dataPerArm.push({ arm_group_index: armIndex, outcome_measure_data: '', number_of_participants_analyzed: '', number_of_units_analyzed: '' });
        armDataIndex = dataPerArm.length - 1;
      }
      dataPerArm[armDataIndex] = { ...dataPerArm[armDataIndex], [field]: value };
      rows[rowIndex] = { ...rows[rowIndex], data_per_arm: dataPerArm };
      measures[measureIndex] = { ...measures[measureIndex], rows };
      return { ...prev, outcome_measures: measures };
    });
  };

  const addStatisticalAnalysis = (measureIndex) => {
    setFormData((prev) => {
      const measures = [...(prev.outcome_measures || [])];
      measures[measureIndex] = {
        ...measures[measureIndex],
        statistical_analyses: [
          ...(measures[measureIndex].statistical_analyses || []),
          {
            comparison_group_selection: [],
            comments_overview: '',
            type_of_statistical_test: '',
            comments_test_type: '',
            p_value: '',
            p_value_comments: '',
            method: '',
            other_method_name: '',
            method_comments: '',
            estimation_parameter: '',
            other_parameter_name: '',
            estimated_value: '',
            confidence_interval: { level: '', number_of_sides: '', lower_limit: '', upper_limit: '', na_explanation: '' },
            parameter_dispersion_type: '',
            dispersion_value: '',
            estimation_comments: '',
            other_statistical_analysis: '',
          },
        ],
      };
      return { ...prev, outcome_measures: measures };
    });
  };

  const removeStatisticalAnalysis = (measureIndex, analysisIndex) => {
    setFormData((prev) => {
      const measures = [...(prev.outcome_measures || [])];
      measures[measureIndex] = {
        ...measures[measureIndex],
        statistical_analyses: (measures[measureIndex].statistical_analyses || []).filter((_, i) => i !== analysisIndex),
      };
      return { ...prev, outcome_measures: measures };
    });
  };

  const updateStatisticalAnalysis = (measureIndex, analysisIndex, field, value) => {
    setFormData((prev) => {
      const measures = [...(prev.outcome_measures || [])];
      const analyses = [...(measures[measureIndex].statistical_analyses || [])];
      if (field === 'confidence_interval') {
        // Special handling for nested object
        analyses[analysisIndex] = { ...analyses[analysisIndex], confidence_interval: value };
      } else {
        analyses[analysisIndex] = { ...analyses[analysisIndex], [field]: value };
      }
      measures[measureIndex] = { ...measures[measureIndex], statistical_analyses: analyses };
      return { ...prev, outcome_measures: measures };
    });
  };

  // ===== Adverse Events Helper Functions =====
  const addAdverseEventArmGroup = () => {
    setFormData((prev) => ({
      ...prev,
      adverse_events: {
        ...prev.adverse_events,
        arms_groups: [...(prev.adverse_events?.arms_groups || []), { title: '', description: '' }],
      },
    }));
  };

  const removeAdverseEventArmGroup = (index) => {
    setFormData((prev) => ({
      ...prev,
      adverse_events: {
        ...prev.adverse_events,
        arms_groups: (prev.adverse_events?.arms_groups || []).filter((_, i) => i !== index),
      },
    }));
  };

  const updateAdverseEventArmGroup = (index, field, value) => {
    setFormData((prev) => {
      const arms = [...(prev.adverse_events?.arms_groups || [])];
      arms[index] = { ...arms[index], [field]: value };
      return {
        ...prev,
        adverse_events: { ...prev.adverse_events, arms_groups: arms },
      };
    });
  };

  const addSeriousAdverseEvent = () => {
    setFormData((prev) => ({
      ...prev,
      adverse_events: {
        ...prev.adverse_events,
        serious_adverse_events: {
          ...prev.adverse_events?.serious_adverse_events,
          events: [
            ...(prev.adverse_events?.serious_adverse_events?.events || []),
            {
              adverse_event_term: '',
              organ_system: '',
              additional_description: '',
              source_vocabulary_name: '',
              collection_approach: '',
              data_per_arm: [], // [{ arm_group_index, number_affected, number_at_risk, number_of_events }]
            },
          ],
        },
      },
    }));
  };

  const removeSeriousAdverseEvent = (index) => {
    setFormData((prev) => ({
      ...prev,
      adverse_events: {
        ...prev.adverse_events,
        serious_adverse_events: {
          ...prev.adverse_events?.serious_adverse_events,
          events: (prev.adverse_events?.serious_adverse_events?.events || []).filter((_, i) => i !== index),
        },
      },
    }));
  };

  const updateSeriousAdverseEvent = (index, field, value) => {
    setFormData((prev) => {
      const events = [...(prev.adverse_events?.serious_adverse_events?.events || [])];
      if (field === 'data_per_arm') {
        events[index] = { ...events[index], data_per_arm: value };
      } else {
        events[index] = { ...events[index], [field]: value };
      }
      return {
        ...prev,
        adverse_events: {
          ...prev.adverse_events,
          serious_adverse_events: { ...prev.adverse_events?.serious_adverse_events, events },
        },
      };
    });
  };

  // Helper to get data_per_arm entry for a specific arm in a serious adverse event
  const getSeriousEventDataForArm = (eventIndex, armIndex) => {
    const event = formData.adverse_events?.serious_adverse_events?.events?.[eventIndex];
    const dataPerArm = event?.data_per_arm || [];
    return dataPerArm.find(d => d.arm_group_index === armIndex) || { arm_group_index: armIndex, number_affected: '', number_at_risk: '', number_of_events: '' };
  };

  // Helper to update data_per_arm entry for a specific arm in a serious adverse event
  const updateSeriousEventDataForArm = (eventIndex, armIndex, field, value) => {
    setFormData((prev) => {
      const events = [...(prev.adverse_events?.serious_adverse_events?.events || [])];
      const dataPerArm = [...(events[eventIndex]?.data_per_arm || [])];
      let armDataIndex = dataPerArm.findIndex(d => d.arm_group_index === armIndex);
      if (armDataIndex === -1) {
        dataPerArm.push({ arm_group_index: armIndex, number_affected: '', number_at_risk: '', number_of_events: '' });
        armDataIndex = dataPerArm.length - 1;
      }
      dataPerArm[armDataIndex] = { ...dataPerArm[armDataIndex], [field]: value };
      events[eventIndex] = { ...events[eventIndex], data_per_arm: dataPerArm };
      return {
        ...prev,
        adverse_events: {
          ...prev.adverse_events,
          serious_adverse_events: { ...prev.adverse_events?.serious_adverse_events, events },
        },
      };
    });
  };

  const addOtherAdverseEvent = () => {
    setFormData((prev) => ({
      ...prev,
      adverse_events: {
        ...prev.adverse_events,
        other_adverse_events: {
          ...prev.adverse_events?.other_adverse_events,
          events: [
            ...(prev.adverse_events?.other_adverse_events?.events || []),
            {
              adverse_event_term: '',
              organ_system: '',
              additional_description: '',
              source_vocabulary_name: '',
              collection_approach: '',
              data_per_arm: [], // [{ arm_group_index, number_affected, number_at_risk, number_of_events }]
            },
          ],
        },
      },
    }));
  };

  const removeOtherAdverseEvent = (index) => {
    setFormData((prev) => ({
      ...prev,
      adverse_events: {
        ...prev.adverse_events,
        other_adverse_events: {
          ...prev.adverse_events?.other_adverse_events,
          events: (prev.adverse_events?.other_adverse_events?.events || []).filter((_, i) => i !== index),
        },
      },
    }));
  };

  const updateOtherAdverseEvent = (index, field, value) => {
    setFormData((prev) => {
      const events = [...(prev.adverse_events?.other_adverse_events?.events || [])];
      if (field === 'data_per_arm') {
        events[index] = { ...events[index], data_per_arm: value };
      } else {
        events[index] = { ...events[index], [field]: value };
      }
      return {
        ...prev,
        adverse_events: {
          ...prev.adverse_events,
          other_adverse_events: { ...prev.adverse_events?.other_adverse_events, events },
        },
      };
    });
  };

  // Helper to get data_per_arm entry for a specific arm in an other adverse event
  const getOtherEventDataForArm = (eventIndex, armIndex) => {
    const event = formData.adverse_events?.other_adverse_events?.events?.[eventIndex];
    const dataPerArm = event?.data_per_arm || [];
    return dataPerArm.find(d => d.arm_group_index === armIndex) || { arm_group_index: armIndex, number_affected: '', number_at_risk: '', number_of_events: '' };
  };

  // Helper to update data_per_arm entry for a specific arm in an other adverse event
  const updateOtherEventDataForArm = (eventIndex, armIndex, field, value) => {
    setFormData((prev) => {
      const events = [...(prev.adverse_events?.other_adverse_events?.events || [])];
      const dataPerArm = [...(events[eventIndex]?.data_per_arm || [])];
      let armDataIndex = dataPerArm.findIndex(d => d.arm_group_index === armIndex);
      if (armDataIndex === -1) {
        dataPerArm.push({ arm_group_index: armIndex, number_affected: '', number_at_risk: '', number_of_events: '' });
        armDataIndex = dataPerArm.length - 1;
      }
      dataPerArm[armDataIndex] = { ...dataPerArm[armDataIndex], [field]: value };
      events[eventIndex] = { ...events[eventIndex], data_per_arm: dataPerArm };
      return {
        ...prev,
        adverse_events: {
          ...prev.adverse_events,
          other_adverse_events: { ...prev.adverse_events?.other_adverse_events, events },
        },
      };
    });
  };

  // Helper to get data_per_arm entry for mortality/totals
  const getMortalityDataForArm = (section, armIndex) => {
    const sectionData = formData.adverse_events?.[section]?.data_per_arm || [];
    return sectionData.find(d => d.arm_group_index === armIndex) || { arm_group_index: armIndex, total_affected: '', total_at_risk: '' };
  };

  // Helper to update data_per_arm entry for mortality/totals
  const updateMortalityDataForArm = (section, armIndex, field, value) => {
    setFormData((prev) => {
      const sectionData = [...(prev.adverse_events?.[section]?.data_per_arm || [])];
      let armDataIndex = sectionData.findIndex(d => d.arm_group_index === armIndex);
      if (armDataIndex === -1) {
        sectionData.push({ arm_group_index: armIndex, total_affected: '', total_at_risk: '' });
        armDataIndex = sectionData.length - 1;
      }
      sectionData[armDataIndex] = { ...sectionData[armDataIndex], [field]: value };
      return {
        ...prev,
        adverse_events: {
          ...prev.adverse_events,
          [section]: { ...prev.adverse_events?.[section], data_per_arm: sectionData },
        },
      };
    });
  };

  // Helper to calculate totals for adverse events
  const calculateAdverseEventTotal = (section, field) => {
    const sectionData = formData.adverse_events?.[section]?.data_per_arm || [];
    let total = 0;
    sectionData.forEach(armData => {
      const value = parseFloat(armData[field]) || 0;
      total += value;
    });
    return total > 0 ? total.toString() : '';
  };

  // Helper function to sanitize numeric values (remove pipe-separated values, extract first number)
  const sanitizeNumericValue = (value) => {
    if (!value || value === '') return '';
    const strValue = String(value);
    // If value contains pipe, extract the first number before the pipe
    if (strValue.includes('|')) {
      const firstPart = strValue.split('|')[0].trim();
      // Try to parse as number, return empty string if invalid
      const num = parseFloat(firstPart);
      return isNaN(num) ? '' : num.toString();
    }
    // If it's already a valid number string, return it
    const num = parseFloat(strValue);
    return isNaN(num) ? '' : num.toString();
  };

  // Convert extraction metadata to programmatic highlights
  // Function to find text in PDF and get its bounding box
  const findTextInPdf = async (pdf, pageNum, searchText) => {
    try {
      const page = await pdf.getPage(pageNum);
      const textContent = await page.getTextContent();
      const viewport = page.getViewport({ scale: 1.0 });
      
      // Build full text and track item positions
      let fullPageText = '';
      const itemPositions = []; // Track which items correspond to which text positions
      
      textContent.items.forEach((item) => {
        const str = item.str || '';
        const startPos = fullPageText.length;
        fullPageText += str + ' '; // Add space between items
        const endPos = fullPageText.length;
        
        const transform = item.transform || [1, 0, 0, 1, 0, 0];
        itemPositions.push({
          startPos,
          endPos,
          str,
          x: transform[4],
          y: transform[5],
          width: item.width || 0,
          height: item.height || Math.abs(transform[3]) || 12,
          item,
        });
      });
      
      // Enhanced normalization: handle variations while preserving structure
      const normalizeForSearch = (text) => {
        if (!text) return '';
        return text
          .toLowerCase()
          .replace(/[^\w\s\-]/g, ' ') // Replace punctuation with spaces, but keep hyphens
          .replace(/\s+/g, ' ')       // Normalize multiple spaces/newlines to single space
          .replace(/\s*-\s*/g, '-')   // Normalize spaces around hyphens
          .trim();
      };
      
      // Build normalized text item-by-item to match how we build the character mapping
      // This ensures consistency between search text and item mapping
      let normalizedFullText = '';
      itemPositions.forEach((itemPos) => {
        const normalizedItemText = normalizeForSearch(itemPos.str);
        normalizedFullText += normalizedItemText + ' ';
      });
      normalizedFullText = normalizedFullText.trim();
      
      const normalizedSearchText = normalizeForSearch(searchText);
      
      // Strategy 1: Try exact normalized match
      let searchIndex = normalizedFullText.indexOf(normalizedSearchText);
      let searchLength = normalizedSearchText.length;
      
      // Strategy 2: If not found, try word-by-word matching (for cases where text might be split)
      if (searchIndex === -1 && normalizedSearchText.length > 0) {
        const searchWords = normalizedSearchText.split(/\s+/).filter(w => w.length > 0);
        if (searchWords.length > 0) {
          // Try to find first word
          const firstWord = searchWords[0];
          let firstWordIndex = normalizedFullText.indexOf(firstWord);
          
          if (firstWordIndex !== -1) {
            // Found first word, now try to match subsequent words nearby
            let currentIndex = firstWordIndex;
            let matchedWords = 1;
            
            for (let i = 1; i < searchWords.length && currentIndex < normalizedFullText.length; i++) {
              const nextWord = searchWords[i];
              // Look for next word within reasonable distance (max 200 chars)
              const searchWindow = normalizedFullText.substring(
                currentIndex + firstWord.length,
                currentIndex + firstWord.length + 200
              );
              const nextWordIndex = searchWindow.indexOf(nextWord);
              
              if (nextWordIndex !== -1) {
                matchedWords++;
                currentIndex = currentIndex + firstWord.length + nextWordIndex;
              } else {
                break;
              }
            }
            
            // If we matched at least 50% of words, use it
            if (matchedWords >= Math.ceil(searchWords.length * 0.5)) {
              searchIndex = firstWordIndex;
              // Estimate length based on matched words
              searchLength = Math.min(
                normalizedFullText.length - searchIndex,
                normalizedSearchText.length * 1.5 // Allow some flexibility
              );
            }
          }
        }
      }
      
      // Strategy 3: Try partial match (first 100 chars)
      if (searchIndex === -1) {
        const partialSearch = normalizedSearchText.substring(0, Math.min(100, normalizedSearchText.length));
        searchIndex = normalizedFullText.indexOf(partialSearch);
        if (searchIndex !== -1) {
          searchLength = partialSearch.length;
        }
      }
      
      // Strategy 4: Try matching significant words only (for very long text snippets)
      if (searchIndex === -1 && normalizedSearchText.length > 50) {
        const searchWords = normalizedSearchText.split(/\s+/).filter(w => w.length > 3); // Only words longer than 3 chars
        if (searchWords.length >= 2) {
          // Try to find a sequence of significant words
          const firstSignificantWord = searchWords[0];
          let firstWordIndex = normalizedFullText.indexOf(firstSignificantWord);
          
          if (firstWordIndex !== -1) {
            // Check if second significant word is nearby
            const secondSignificantWord = searchWords[1];
            const nearbyText = normalizedFullText.substring(
              firstWordIndex,
              firstWordIndex + 300
            );
            if (nearbyText.indexOf(secondSignificantWord) !== -1) {
              searchIndex = firstWordIndex;
              searchLength = Math.min(300, normalizedFullText.length - searchIndex);
            }
          }
        }
      }
      
      if (searchIndex === -1) {
        console.warn(`[SubmitResults] Text not found on page ${pageNum}. Search text: "${searchText.substring(0, 100)}..."`);
        console.warn(`[SubmitResults] First 200 chars of page text: "${normalizedFullText.substring(0, 200)}..."`);
        return null;
      }
      
      const searchEndIndex = searchIndex + searchLength;
      
      // Build normalized text with item index tracking
      // Track which item index each character in normalized text corresponds to
      let normalizedCharToItemIndex = [];
      itemPositions.forEach((itemPos, itemIndex) => {
        const normalizedItemText = normalizeForSearch(itemPos.str);
        // Each character in normalized item text maps to this item index
        for (let i = 0; i < normalizedItemText.length; i++) {
          normalizedCharToItemIndex.push(itemIndex);
        }
        // Space after item also maps to this item index
        normalizedCharToItemIndex.push(itemIndex);
      });
      
      // Find unique item indices that correspond to the search range
      const matchedItemIndices = new Set();
      for (let i = searchIndex; i < Math.min(searchEndIndex, normalizedCharToItemIndex.length); i++) {
        if (normalizedCharToItemIndex[i] !== undefined) {
          matchedItemIndices.add(normalizedCharToItemIndex[i]);
        }
      }
      
      const matchedItems = Array.from(matchedItemIndices).map(idx => itemPositions[idx]);
      
      if (matchedItems.length === 0) {
        console.warn(`[SubmitResults] No items matched for text on page ${pageNum}`);
        return null;
      }
      
      // Calculate bounding box from matched items
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      
      matchedItems.forEach(itemPos => {
        // PDF coordinates: origin at bottom-left, Y increases upward
        // Convert to viewport coordinates with origin at top-left
        const x = itemPos.x;
        const y = viewport.height - itemPos.y; // Flip Y
        const width = itemPos.width;
        const height = itemPos.height;
        
        minX = Math.min(minX, x);
        minY = Math.min(minY, y - height); // Text baseline is at y, height extends upward
        maxX = Math.max(maxX, x + width);
        maxY = Math.max(maxY, y);
      });
      
      if (minX === Infinity || maxX <= minX || maxY <= minY) {
        console.warn(`[SubmitResults] Invalid bounding box calculated`);
        return null;
      }
      
      // Convert to normalized coordinates (0-1)
      const normalizedLeft = minX / viewport.width;
      const normalizedTop = minY / viewport.height;
      const normalizedWidth = (maxX - minX) / viewport.width;
      const normalizedHeight = (maxY - minY) / viewport.height;
      
      console.log(`[SubmitResults] ✓ Found text on page ${pageNum}:`, {
        searchText: searchText.substring(0, 60) + '...',
        matchedItems: matchedItems.length,
        bounds: {
          left: normalizedLeft.toFixed(3),
          top: normalizedTop.toFixed(3),
          width: normalizedWidth.toFixed(3),
          height: normalizedHeight.toFixed(3)
        }
      });
      
      return {
        left: Math.max(0, normalizedLeft - 0.005), // Minimal padding
        top: Math.max(0, normalizedTop - 0.003),
        width: Math.min(1, normalizedWidth + 0.01),
        height: Math.min(1, normalizedHeight + 0.006),
      };
    } catch (error) {
      console.error(`[SubmitResults] Error finding text in PDF page ${pageNum}:`, error);
      return null;
    }
  };

  /**
   * Convert extraction metadata to programmatic highlights
   * 
   * Purpose: Show users exactly where in the PDF each extracted field's data came from.
   * This helps users:
   * 1. Verify extraction accuracy by seeing the source text
   * 2. Understand which parts of the document were used
   * 3. Navigate to source locations when clicking form fields
   * 4. Quality check the AI extraction results
   */
  const convertMetadataToHighlights = async (metadata) => {
    if (!fileInfo?.azureUrl) {
      console.warn('[SubmitResults] No PDF URL available for text-based highlighting, using fallback');
      // Fallback to approximate highlights
      convertMetadataToHighlightsApproximate(metadata);
      return;
    }

    const highlights = [];
    console.log('[SubmitResults] Converting metadata to highlights with precise text search:', metadata);
    
    // Load PDF for text search
    try {
      const loadingTask = pdfjsLib.getDocument({ url: fileInfo.azureUrl, isEvalSupported: false });
      const pdf = await loadingTask.promise;
      
      console.log(`[SubmitResults] PDF loaded successfully. Total pages: ${pdf.numPages}`);
      
      let successCount = 0;
      let failCount = 0;
      
      for (const fieldPath of Object.keys(metadata)) {
        const meta = metadata[fieldPath];
        if (meta && meta.page && meta.textSnippet) {
          const pageNum = typeof meta.page === 'string' ? parseInt(meta.page, 10) : meta.page;
          if (isNaN(pageNum) || pageNum < 1 || pageNum > pdf.numPages) {
            console.warn(`[SubmitResults] Invalid page number for ${fieldPath}:`, meta.page);
            failCount++;
            continue;
          }
          
          // Try to find the exact text in the PDF
          const bounds = await findTextInPdf(pdf, pageNum, meta.textSnippet);
          
          if (bounds) {
            const highlight = {
              page: pageNum,
              left: bounds.left,
              top: bounds.top,
              width: bounds.width,
              height: bounds.height,
              comment: {
                text: meta.textSnippet || `Source for: ${fieldPath}`,
                author: { firstName: 'System', lastName: 'Extraction' },
                timestamp: new Date().toISOString(),
              },
              isProgrammatic: true,
              fieldPath: fieldPath,
            };
            highlights.push(highlight);
            successCount++;
            console.log(`[SubmitResults] ✓ Created precise highlight for ${fieldPath} on page ${pageNum}`);
          } else {
            // Fallback: Text not found, skip highlighting this field
            failCount++;
            console.warn(`[SubmitResults] ✗ Could not find text for ${fieldPath} on page ${pageNum}. Text snippet: "${meta.textSnippet?.substring(0, 100)}..."`);
          }
        } else {
          console.log(`[SubmitResults] No page/textSnippet metadata for ${fieldPath}:`, meta);
          failCount++;
        }
      }
      
      console.log(`[SubmitResults] Highlight creation summary: ${successCount} succeeded, ${failCount} failed out of ${Object.keys(metadata).length} total fields`);
      
      console.log(`[SubmitResults] Total programmatic highlights created: ${highlights.length}`, highlights);
      setProgrammaticHighlights(highlights);
      
      // Save programmatic highlights to backend
      if (highlights.length > 0 && projectId) {
        try {
          const pdfId = `submit-results-${projectId}`;
          // Fetch existing highlights first to merge with programmatic ones
          const existingRes = await fetch(`/api/pdf-highlights?pdfId=${encodeURIComponent(pdfId)}`);
          let existingHighlights = [];
          if (existingRes.ok) {
            const existingData = await existingRes.json();
            existingHighlights = existingData.highlights || [];
          }
          
          // Merge: keep user highlights (not programmatic) and add new programmatic highlights
          const userHighlights = existingHighlights.filter(h => !h.isProgrammatic);
          const allHighlights = [...userHighlights, ...highlights];
          
          // Save all highlights
          const saveRes = await fetch('/api/pdf-highlights', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ pdfId, highlights: allHighlights }),
          });
          
          if (saveRes.ok && saveRes.status !== 405) {
            const contentType = saveRes.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
              console.log(`[SubmitResults] Saved ${highlights.length} programmatic highlights to backend`);
            } else {
              // Silently handle - API may not be available
            }
          } else {
            // Silently handle 405 or other errors - API endpoint may not exist
          }
        } catch (saveError) {
          // Silently handle save errors - highlights will still work, just won't be persisted
          console.warn('[SubmitResults] Error saving programmatic highlights (non-critical):', saveError.message);
        }
      }
      
      // Auto-navigate to first page with highlights if PDF viewer is ready
      if (highlights.length > 0 && pdfViewerRef.current) {
        const firstPage = highlights[0].page;
        setTimeout(() => {
          if (pdfViewerRef.current) {
            pdfViewerRef.current.navigateToPage(firstPage);
          }
        }, 500);
      }
    } catch (error) {
      console.error('[SubmitResults] Error loading PDF for text search:', error);
      // Fallback to approximate highlights
      convertMetadataToHighlightsApproximate(metadata);
    }
  };

  // Fallback function for approximate highlights when PDF is not ready
  // Note: This creates small indicator highlights - precise highlighting requires PDF to be loaded
  const convertMetadataToHighlightsApproximate = (metadata) => {
    const highlights = [];
    console.log('[SubmitResults] Using approximate highlights (PDF not ready for text search)');
    Object.keys(metadata).forEach(fieldPath => {
      const meta = metadata[fieldPath];
      if (meta && meta.page) {
        const pageNum = typeof meta.page === 'string' ? parseInt(meta.page, 10) : meta.page;
        if (isNaN(pageNum) || pageNum < 1) return;
        
        // Create a small indicator highlight (not a large generic one)
        // This will be replaced with precise highlights once PDF loads
        const highlight = {
          page: pageNum,
          left: 0.05, // Small left margin indicator
          top: 0.05,  // Small top margin indicator
          width: 0.02, // Very small width - just an indicator
          height: 0.02, // Very small height - just an indicator
          comment: {
            text: `Source on page ${pageNum}: ${meta.textSnippet?.substring(0, 100) || fieldPath}`,
            author: { firstName: 'System', lastName: 'Extraction' },
            timestamp: new Date().toISOString(),
          },
          isProgrammatic: true,
          fieldPath: fieldPath,
        };
        highlights.push(highlight);
      }
    });
    setProgrammaticHighlights(highlights);
    console.log(`[SubmitResults] Created ${highlights.length} approximate indicator highlights`);
  };

  // Handle field click/focus to navigate to PDF page and highlight
  const handleFieldClick = (fieldPath) => {
    const meta = extractionMetadata[fieldPath];
    if (meta && meta.page && pdfViewerRef.current) {
      const pageNum = typeof meta.page === 'string' ? parseInt(meta.page, 10) : meta.page;
      pdfViewerRef.current.navigateToPage(pageNum);
      
      // Try to find and scroll to the highlight for this field
      setTimeout(() => {
        const highlight = programmaticHighlights.find(h => h.fieldPath === fieldPath);
        if (highlight) {
          // The PDF viewer will handle scrolling to the page
          // We can add additional scroll-to-highlight logic here if needed
          console.log(`[SubmitResults] Navigated to page ${pageNum} for field: ${fieldPath}`);
        }
      }, 300);
    }
  };

  // Get metadata for a field path
  const getFieldMetadata = (fieldPath) => {
    return extractionMetadata[fieldPath] || null;
  };

  // Check if field has source data (for styling/indicators)
  const hasFieldSource = (fieldPath) => {
    return !!extractionMetadata[fieldPath];
  };

  // Helper to get field path for nested fields (e.g., arms_groups[0].title)
  const getNestedFieldPath = (basePath, index, fieldName) => {
    return `${basePath}[${index}].${fieldName}`;
  };

  // Helper to get field path for deeply nested fields (e.g., periods[0].milestones[0].data)
  const getDeepNestedFieldPath = (basePath, index, nestedPath, nestedIndex, fieldName) => {
    return `${basePath}[${index}].${nestedPath}[${nestedIndex}].${fieldName}`;
  };

  // Helper to add field navigation props to any TextField
  const getFieldNavigationProps = (fieldPath, currentValue = '') => {
    const hasSource = hasFieldSource(fieldPath);
    const meta = getFieldMetadata(fieldPath);
    
    return {
      onFocus: () => handleFieldClick(fieldPath),
      sx: {
        '& .MuiInputBase-root': {
          cursor: hasSource ? 'pointer' : 'text',
        }
      },
      helperText: hasSource && meta?.page
        ? `📄 Focus to view source on page ${meta.page}${currentValue ? ` | ${currentValue}` : ''}`
        : currentValue || undefined
    };
  };

  const loadFormData = async () => {
    if (!projectId) return;
    try {
      setLoading(true);
      const data = await submitResultsService.getByProject(projectId);
        if (data && data.recruitment_details !== undefined) {
          // Ensure periods have Started and Completed milestones
          const normalizedPeriods = data.periods && data.periods.length > 0 
            ? data.periods.map(period => {
                // Check if period has milestones array
                if (!period.milestones || period.milestones.length === 0) {
                  // If no milestones, create default structure
                  return {
                    ...period,
                    milestones: [
                      { title: 'Started', data: '', comments: '', is_required: true },
                      { title: 'Completed', data: '', comments: '', is_required: true },
                    ],
                  };
                }
                // Ensure Started and Completed milestones exist
                const hasStarted = period.milestones.some(m => m.title === 'Started' && m.is_required);
                const hasCompleted = period.milestones.some(m => m.title === 'Completed' && m.is_required);
                const additionalMilestones = period.milestones.filter(m => !m.is_required);
                
                return {
                  ...period,
                  milestones: [
                    hasStarted 
                      ? { ...period.milestones.find(m => m.title === 'Started' && m.is_required), data: sanitizeNumericValue(period.milestones.find(m => m.title === 'Started' && m.is_required)?.data) }
                      : { title: 'Started', data: '', comments: '', is_required: true },
                    hasCompleted
                      ? { ...period.milestones.find(m => m.title === 'Completed' && m.is_required), data: sanitizeNumericValue(period.milestones.find(m => m.title === 'Completed' && m.is_required)?.data) }
                      : { title: 'Completed', data: '', comments: '', is_required: true },
                    ...additionalMilestones.map(m => ({ ...m, data: sanitizeNumericValue(m.data) })),
                  ],
                  reasons_not_completed: (period.reasons_not_completed || []).map(reason => ({
                    ...reason,
                    data: sanitizeNumericValue(reason.data),
                  })),
                };
              })
            : [{
                title: '',
                milestones: [
                  { title: 'Started', data: '', comments: '', is_required: true },
                  { title: 'Completed', data: '', comments: '', is_required: true },
                ],
                not_completed: { number: '' },
                reasons_not_completed: [],
              }];
          
          // Normalize baseline characteristics measures - migrate old structure to rows array
          const normalizedBaselineMeasures = data.baseline_characteristics?.measures?.map(measure => {
            // If measure has old structure (category_or_row_title at measure level), migrate to rows
            // Migrate old flat structure to new nested structure
            if (measure.category_or_row_title !== undefined && !measure.rows) {
              return {
                ...measure,
                rows: [
                  {
                    category_or_row_title: measure.category_or_row_title || '',
                    data_per_arm: [], // Initialize empty, user will need to enter per-arm data
                    na_explanation: measure.na_explanation || '',
                  },
                ],
                // Remove old fields
                category_or_row_title: undefined,
                baseline_measure_data: undefined,
                na_explanation: undefined,
              };
            }
            // Migrate rows that have old flat baseline_measure_data to new data_per_arm structure
            if (measure.rows && measure.rows.length > 0) {
              return {
                ...measure,
                rows: measure.rows.map(row => {
                  // If row has old flat baseline_measure_data but no data_per_arm, migrate it
                  if (row.baseline_measure_data !== undefined && !row.data_per_arm) {
                    return {
                      ...row,
                      data_per_arm: [], // Initialize empty, old data can't be automatically mapped to arms
                      baseline_measure_data: undefined,
                    };
                  }
                  // Ensure data_per_arm exists
                  if (!row.data_per_arm) {
                    return {
                      ...row,
                      data_per_arm: [],
                    };
                  }
                  return row;
                }),
              };
            }
            // Ensure rows array exists
            if (!measure.rows || measure.rows.length === 0) {
              return {
                ...measure,
                rows: [
                  {
                    category_or_row_title: '',
                    data_per_arm: [],
                    na_explanation: '',
                  },
                ],
              };
            }
            return measure;
          }) || [];

          setFormData({
            recruitment_details: data.recruitment_details || '',
            pre_assignment_details: data.pre_assignment_details || '',
            arms_groups: (data.arms_groups && data.arms_groups.length > 0) ? data.arms_groups : [{ title: '', description: '', type_of_units_assigned: '' }],
            periods: normalizedPeriods || initialFormData.periods,
            baseline_characteristics: {
              arms_groups: (data.baseline_characteristics?.arms_groups && Array.isArray(data.baseline_characteristics.arms_groups)) 
                ? data.baseline_characteristics.arms_groups 
                : initialFormData.baseline_characteristics.arms_groups,
              analysis_population: data.baseline_characteristics?.analysis_population || initialFormData.baseline_characteristics.analysis_population,
              measures: Array.isArray(normalizedBaselineMeasures) ? normalizedBaselineMeasures : [],
            },
            outcome_measures: (data.outcome_measures && Array.isArray(data.outcome_measures)) ? data.outcome_measures : [],
            adverse_events: (data.adverse_events && typeof data.adverse_events === 'object') ? data.adverse_events : initialFormData.adverse_events || {
              time_frame: '',
              adverse_event_reporting_description: '',
              source_vocabulary_name_default: '',
              collection_approach_default: '',
              arms_groups: [],
              all_cause_mortality: { data_per_arm: [] },
              serious_adverse_events: { data_per_arm: [], events: [] },
              other_adverse_events: { frequency_threshold: '', data_per_arm: [], events: [] },
            },
          });
        if (data.fileInfo) {
          setFileInfo(data.fileInfo);
        }
        if (data.extractionMetadata) {
          setExtractionMetadata(data.extractionMetadata);
          // Highlights will be generated by useEffect when both fileInfo and extractionMetadata are available
        }
      }
    } catch (err) {
      console.error('Error loading form data:', err);
      setUploadError('Failed to load form data.');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadedFile(file);
      setUploadError(null);
      setUploadSuccess(null);
      setFileValidationStatus(null); // Reset validation status when file changes
      
      // Automatically validate the file when selected
      await validateFile(file);
    }
  };

  // Validate file for readability before upload
  const validateFile = async (file) => {
    if (!file) {
      return { isValid: false, reason: 'No file selected' };
    }

    if (!projectId) {
      return { isValid: false, reason: 'Project ID is required for validation' };
    }

    setIsValidatingFile(true);
    setFileValidationStatus(null);

    try {
      // First, upload the file temporarily to get it to the server for validation
      // Skip database save since this is just for validation
      const formData = new FormData();
      formData.append('file', file);
      formData.append('projectId', projectId);
      formData.append('documentType', 'user_defined'); // Use valid enum value for validation
      formData.append('title', file.name);
      formData.append('skipSave', 'true'); // Skip saving to database during validation

      // Upload file temporarily
      const uploadResponse = await axios.post(API_ENDPOINTS.documents.upload, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (!uploadResponse.data || !uploadResponse.data.file) {
        return { isValid: false, reason: 'Failed to upload file for validation' };
      }

      const uploadedFileInfo = uploadResponse.data.file;

      // Now validate the uploaded file
      const validationResponse = await axios.post(API_ENDPOINTS.ai.validateDocuments, {
        files: [{
          filename: uploadedFileInfo.filename || uploadedFileInfo.azureBlobName,
          originalname: file.name,
          azureBlobName: uploadedFileInfo.azureBlobName || uploadedFileInfo.filename,
          mimeType: file.type,
          documentType: 'submit_results' // Generic type for submit results
        }]
      });

      if (validationResponse.data.success && validationResponse.data.validationResults) {
        const result = validationResponse.data.validationResults[0];
        const validationStatus = {
          isValid: result.isValid || false,
          reason: result.reason || (result.isValid ? 'File is readable' : 'File validation failed'),
          detectedType: result.detectedType,
        };

        setFileValidationStatus(validationStatus);
        return validationStatus;
      } else {
        const status = { isValid: false, reason: 'Validation API returned an error' };
        setFileValidationStatus(status);
        return status;
      }
    } catch (error) {
      console.error('[SubmitResults] Error validating file:', error);
      const status = {
        isValid: false,
        reason: error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to validate file'
      };
      setFileValidationStatus(status);
      return status;
    } finally {
      setIsValidatingFile(false);
    }
  };

  const handleFileUpload = async () => {
    if (!uploadedFile) {
      setUploadError('Please select a file first.');
      return;
    }
    if (!projectId) {
      setUploadError('Project ID is missing. Please select a project.');
      return;
    }

    // STEP 0: Validate file for readability first (if not already validated)
    if (!fileValidationStatus || !fileValidationStatus.isValid) {
      const validationResult = await validateFile(uploadedFile);
      
      if (!validationResult.isValid) {
        setUploadError(`File validation failed: ${validationResult.reason}. Please upload a file with readable text.`);
        setUploading(false);
        return;
      }
    }

    setUploading(true);
    setUploadError(null);
    setUploadSuccess(null);

    try {
      const response = await submitResultsService.uploadAndExtract(uploadedFile, projectId);

      if (response.success) {
        setFileInfo(response.data.fileInfo);
        
        // Set extraction metadata if available
        if (response.data.extractionMetadata) {
          setExtractionMetadata(response.data.extractionMetadata);
          convertMetadataToHighlights(response.data.extractionMetadata).catch(err => {
            console.error('[SubmitResults] Error converting metadata to highlights:', err);
          });
        }
        
        // Auto-populate form with extracted data
        if (response.extractedData) {
          // Normalize baseline measures to ensure rows array structure
          const normalizedExtractedMeasures = response.extractedData.baseline_characteristics?.measures?.map(measure => {
            if (measure.category_or_row_title !== undefined && !measure.rows) {
              return {
                ...measure,
                rows: [
                  {
                    category_or_row_title: measure.category_or_row_title || '',
                    baseline_measure_data: measure.baseline_measure_data || '',
                    na_explanation: measure.na_explanation || '',
                  },
                ],
              };
            }
            if (!measure.rows || measure.rows.length === 0) {
              return {
                ...measure,
                rows: [
                  {
                    category_or_row_title: '',
                    baseline_measure_data: '',
                    na_explanation: '',
                  },
                ],
              };
            }
            return measure;
          }) || [];

          setFormData((prev) => ({
            ...prev,
            ...response.extractedData,
            baseline_characteristics: {
              arms_groups: (response.extractedData.baseline_characteristics?.arms_groups && Array.isArray(response.extractedData.baseline_characteristics.arms_groups))
                ? response.extractedData.baseline_characteristics.arms_groups
                : ((prev.baseline_characteristics?.arms_groups && Array.isArray(prev.baseline_characteristics.arms_groups)) ? prev.baseline_characteristics.arms_groups : []),
              analysis_population: response.extractedData.baseline_characteristics?.analysis_population || prev.baseline_characteristics?.analysis_population || {
                type_of_units_analyzed: '',
                analysis_population_description: '',
              },
              measures: Array.isArray(normalizedExtractedMeasures) ? normalizedExtractedMeasures : [],
            },
            outcome_measures: (response.extractedData.outcome_measures && Array.isArray(response.extractedData.outcome_measures)) 
              ? response.extractedData.outcome_measures 
              : ((prev.outcome_measures && Array.isArray(prev.outcome_measures)) ? prev.outcome_measures : []),
            adverse_events: response.extractedData.adverse_events || prev.adverse_events || {
              time_frame: '',
              adverse_event_reporting_description: '',
              source_vocabulary_name_default: '',
              collection_approach_default: '',
              arms_groups: [{ title: '', description: '' }],
              all_cause_mortality: { data_per_arm: [] },
              serious_adverse_events: {
                data_per_arm: [],
                events: [],
              },
              other_adverse_events: {
                frequency_threshold: '',
                data_per_arm: [],
                events: [],
              },
            },
          }));
        } else if (response.data) {
          // If extractedData is not available, use the saved data
          // Normalize periods to ensure Started and Completed milestones exist
          const normalizedPeriods = response.data.periods && response.data.periods.length > 0 
            ? response.data.periods.map(period => {
                if (!period.milestones || period.milestones.length === 0) {
                  return {
                    ...period,
                    milestones: [
                      { title: 'Started', data: '', comments: '', is_required: true },
                      { title: 'Completed', data: '', comments: '', is_required: true },
                    ],
                  };
                }
                const hasStarted = period.milestones.some(m => m.title === 'Started' && m.is_required);
                const hasCompleted = period.milestones.some(m => m.title === 'Completed' && m.is_required);
                const additionalMilestones = period.milestones.filter(m => !m.is_required);
                
                return {
                  ...period,
                  milestones: [
                    hasStarted 
                      ? { ...period.milestones.find(m => m.title === 'Started' && m.is_required), data: sanitizeNumericValue(period.milestones.find(m => m.title === 'Started' && m.is_required)?.data) }
                      : { title: 'Started', data: '', comments: '', is_required: true },
                    hasCompleted
                      ? { ...period.milestones.find(m => m.title === 'Completed' && m.is_required), data: sanitizeNumericValue(period.milestones.find(m => m.title === 'Completed' && m.is_required)?.data) }
                      : { title: 'Completed', data: '', comments: '', is_required: true },
                    ...additionalMilestones.map(m => ({ ...m, data: sanitizeNumericValue(m.data) })),
                  ],
                  reasons_not_completed: (period.reasons_not_completed || []).map(reason => ({
                    ...reason,
                    data: sanitizeNumericValue(reason.data),
                  })),
                };
              })
            : [{
                title: '',
                milestones: [
                  { title: 'Started', data: '', comments: '', is_required: true },
                  { title: 'Completed', data: '', comments: '', is_required: true },
                ],
                not_completed: { number: '' },
                reasons_not_completed: [],
              }];
          
          // Normalize baseline measures
          const normalizedResponseMeasures = response.data.baseline_characteristics?.measures?.map(measure => {
            if (measure.category_or_row_title !== undefined && !measure.rows) {
              return {
                ...measure,
                rows: [
                  {
                    category_or_row_title: measure.category_or_row_title || '',
                    baseline_measure_data: measure.baseline_measure_data || '',
                    na_explanation: measure.na_explanation || '',
                  },
                ],
              };
            }
            if (!measure.rows || measure.rows.length === 0) {
              return {
                ...measure,
                rows: [
                  {
                    category_or_row_title: '',
                    baseline_measure_data: '',
                    na_explanation: '',
                  },
                ],
              };
            }
            return measure;
          }) || [];

          setFormData({
            recruitment_details: response.data.recruitment_details || '',
            pre_assignment_details: response.data.pre_assignment_details || '',
            arms_groups: (response.data.arms_groups && Array.isArray(response.data.arms_groups) && response.data.arms_groups.length > 0) 
              ? response.data.arms_groups 
              : [{ title: '', description: '', type_of_units_assigned: '' }],
            periods: Array.isArray(normalizedPeriods) ? normalizedPeriods : initialFormData.periods,
            baseline_characteristics: {
              arms_groups: (response.data.baseline_characteristics?.arms_groups && Array.isArray(response.data.baseline_characteristics.arms_groups)) 
                ? response.data.baseline_characteristics.arms_groups 
                : [],
              analysis_population: response.data.baseline_characteristics?.analysis_population || {
                type_of_units_analyzed: '',
                analysis_population_description: '',
              },
              measures: Array.isArray(normalizedResponseMeasures) ? normalizedResponseMeasures : [],
            },
            outcome_measures: (response.data.outcome_measures && Array.isArray(response.data.outcome_measures)) 
              ? response.data.outcome_measures 
              : [],
            adverse_events: response.data.adverse_events || {
              time_frame: '',
              adverse_event_reporting_description: '',
              source_vocabulary_name_default: '',
              collection_approach_default: '',
              arms_groups: [{ title: '', description: '' }],
              all_cause_mortality: { data_per_arm: [] },
              serious_adverse_events: {
                data_per_arm: [],
                events: [],
              },
              other_adverse_events: {
                frequency_threshold: '',
                data_per_arm: [],
                events: [],
              },
            },
          });
        }
        setUploadSuccess('File uploaded and data extracted successfully.');
        setUploadedFile(null);
      } else {
        setUploadError(response.error || 'Upload failed');
      }
    } catch (err) {
      setUploadError(err.response?.data?.error || err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleInputChange = (path, value) => {
    setFormData((prev) => {
      const newData = { ...prev };
      const keys = path.split('.');
      let current = newData;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) {
          current[keys[i]] = {};
        }
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  const handleArrayChange = (arrayPath, index, field, value) => {
    setFormData((prev) => {
      const newData = { ...prev };
      const keys = arrayPath.split('.');
      let current = newData;
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }
      const array = [...current[keys[keys.length - 1]]];
      array[index] = { ...array[index], [field]: value };
      current[keys[keys.length - 1]] = array;
      return newData;
    });
  };

  const handleNestedArrayChange = (arrayPath, index, nestedPath, field, value) => {
    setFormData((prev) => {
      const newData = { ...prev };
      const keys = arrayPath.split('.');
      let current = newData;
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }
      const array = [...current[keys[keys.length - 1]]];
      const nestedObj = { ...array[index][nestedPath] };
      nestedObj[field] = value;
      array[index] = { ...array[index], [nestedPath]: nestedObj };
      current[keys[keys.length - 1]] = array;
      return newData;
    });
  };

  const addArmGroup = () => {
    setFormData((prev) => ({
      ...prev,
      arms_groups: [
        ...prev.arms_groups,
        { title: '', description: '', type_of_units_assigned: '' },
      ],
    }));
  };

  const removeArmGroup = (index) => {
    setFormData((prev) => ({
      ...prev,
      arms_groups: prev.arms_groups.filter((_, i) => i !== index),
    }));
  };

  const addPeriod = () => {
    setFormData((prev) => ({
      ...prev,
      periods: [
        ...prev.periods,
        {
          title: '',
          milestones: [
            { title: 'Started', data: '', comments: '', is_required: true },
            { title: 'Completed', data: '', comments: '', is_required: true },
          ],
          not_completed: { number: '' },
          reasons_not_completed: [],
        },
      ],
    }));
  };

  const removePeriod = (index) => {
    setFormData((prev) => ({
      ...prev,
      periods: prev.periods.filter((_, i) => i !== index),
    }));
  };

  const addMilestone = (periodIndex) => {
    setFormData((prev) => {
      const newData = { ...prev };
      const period = { ...newData.periods[periodIndex] };
      period.milestones = [
        ...(period.milestones || []),
        { title: '', data: '', comments: '', is_required: false },
      ];
      newData.periods[periodIndex] = period;
      return newData;
    });
  };

  const removeMilestone = (periodIndex, milestoneIndex) => {
    setFormData((prev) => {
      const newData = { ...prev };
      const period = { ...newData.periods[periodIndex] };
      const milestone = period.milestones[milestoneIndex];
      // Don't allow removal of required milestones (Started and Completed)
      if (milestone && milestone.is_required) {
        return newData;
      }
      period.milestones = period.milestones.filter((_, i) => i !== milestoneIndex);
      newData.periods[periodIndex] = period;
      return newData;
    });
  };

  const addReasonNotCompleted = (periodIndex) => {
    setFormData((prev) => {
      const newData = { ...prev };
      const period = { ...newData.periods[periodIndex] };
      period.reasons_not_completed = [
        ...(period.reasons_not_completed || []),
        { type: '', other_reason: '', data: '' },
      ];
      newData.periods[periodIndex] = period;
      return newData;
    });
  };

  const removeReasonNotCompleted = (periodIndex, reasonIndex) => {
    setFormData((prev) => {
      const newData = { ...prev };
      const period = { ...newData.periods[periodIndex] };
      period.reasons_not_completed = period.reasons_not_completed.filter(
        (_, i) => i !== reasonIndex
      );
      newData.periods[periodIndex] = period;
      return newData;
    });
  };

  // Calculate Not Completed automatically from Started and Completed milestones
  const calculateNotCompleted = (periodIndex) => {
    const period = formData.periods[periodIndex];
    if (!period.milestones || period.milestones.length === 0) return 0;

    const startedMilestone = period.milestones.find(
      (m) => m.title === 'Started' && m.is_required
    );
    const completedMilestone = period.milestones.find(
      (m) => m.title === 'Completed' && m.is_required
    );

    const started = parseInt(startedMilestone?.data || 0);
    const completed = parseInt(completedMilestone?.data || 0);
    const notCompleted = started - completed;
    return notCompleted >= 0 ? notCompleted : 0;
  };

  // Baseline Characteristics helpers
  const addBaselineArmGroup = () => {
    setFormData((prev) => ({
      ...prev,
      baseline_characteristics: {
        ...prev.baseline_characteristics,
        arms_groups: [
          ...prev.baseline_characteristics.arms_groups,
          { title: '', description: '' },
        ],
      },
    }));
  };

  const removeBaselineArmGroup = (index) => {
    setFormData((prev) => ({
      ...prev,
      baseline_characteristics: {
        ...prev.baseline_characteristics,
        arms_groups: prev.baseline_characteristics.arms_groups.filter(
          (_, i) => i !== index
        ),
      },
    }));
  };

  const updateBaselineArmGroup = (index, field, value) => {
    setFormData((prev) => {
      const arms = [...prev.baseline_characteristics.arms_groups];
      arms[index] = { ...arms[index], [field]: value };
      return {
        ...prev,
        baseline_characteristics: {
          ...prev.baseline_characteristics,
          arms_groups: arms,
        },
      };
    });
  };

  const addBaselineMeasure = () => {
    setFormData((prev) => ({
      ...prev,
      baseline_characteristics: {
        ...prev.baseline_characteristics,
        measures: [
          ...prev.baseline_characteristics.measures,
          {
            measure_title: '',
            measure_sub_type: '',
            study_specific_title: '',
            description: '',
            measure_type: '',
            dispersion_type: '',
            number_of_baseline_participants: '',
            number_of_units_analyzed: '',
            analysis_population_type: '',
            measure_analysis_population_description: '',
            unit_of_measure: '',
            rows: [
              {
                category_or_row_title: '',
                baseline_measure_data: '',
                na_explanation: '',
              },
            ],
          },
        ],
      },
    }));
  };

  const addBaselineMeasureRow = (measureIndex) => {
    setFormData((prev) => {
      const measures = [...prev.baseline_characteristics.measures];
      const measure = { ...measures[measureIndex] };
      measure.rows = [
        ...(measure.rows || []),
        {
          category_or_row_title: '',
          baseline_measure_data: '',
          na_explanation: '',
        },
      ];
      measures[measureIndex] = measure;
      return {
        ...prev,
        baseline_characteristics: {
          ...prev.baseline_characteristics,
          measures,
        },
      };
    });
  };

  const removeBaselineMeasureRow = (measureIndex, rowIndex) => {
    setFormData((prev) => {
      const measures = [...prev.baseline_characteristics.measures];
      const measure = { ...measures[measureIndex] };
      if (measure.rows && measure.rows.length > 1) {
        measure.rows = measure.rows.filter((_, i) => i !== rowIndex);
        measures[measureIndex] = measure;
      }
      return {
        ...prev,
        baseline_characteristics: {
          ...prev.baseline_characteristics,
          measures,
        },
      };
    });
  };

  const updateBaselineMeasureRow = (measureIndex, rowIndex, field, value) => {
    setFormData((prev) => {
      const measures = [...prev.baseline_characteristics.measures];
      const measure = { ...measures[measureIndex] };
      const rows = [...(measure.rows || [])];
      if (field === 'data_per_arm') {
        rows[rowIndex] = { ...rows[rowIndex], data_per_arm: value };
      } else {
        rows[rowIndex] = { ...rows[rowIndex], [field]: value };
      }
      measure.rows = rows;
      measures[measureIndex] = measure;
      return {
        ...prev,
        baseline_characteristics: {
          ...prev.baseline_characteristics,
          measures,
        },
      };
    });
  };

  // Helper to get data_per_arm entry for a specific arm in a baseline measure row
  const getBaselineRowDataForArm = (measureIndex, rowIndex, armIndex) => {
    const measure = formData.baseline_characteristics?.measures?.[measureIndex];
    const row = measure?.rows?.[rowIndex];
    const dataPerArm = row?.data_per_arm || [];
    return dataPerArm.find(d => d.arm_group_index === armIndex) || { arm_group_index: armIndex, baseline_measure_data: '' };
  };

  // Helper to update data_per_arm entry for a specific arm in a baseline measure row
  const updateBaselineRowDataForArm = (measureIndex, rowIndex, armIndex, field, value) => {
    setFormData((prev) => {
      const measures = [...prev.baseline_characteristics.measures];
      const measure = { ...measures[measureIndex] };
      const rows = [...(measure.rows || [])];
      const dataPerArm = [...(rows[rowIndex]?.data_per_arm || [])];
      let armDataIndex = dataPerArm.findIndex(d => d.arm_group_index === armIndex);
      if (armDataIndex === -1) {
        dataPerArm.push({ arm_group_index: armIndex, baseline_measure_data: '' });
        armDataIndex = dataPerArm.length - 1;
      }
      dataPerArm[armDataIndex] = { ...dataPerArm[armDataIndex], [field]: value };
      rows[rowIndex] = { ...rows[rowIndex], data_per_arm: dataPerArm };
      measure.rows = rows;
      measures[measureIndex] = measure;
      return {
        ...prev,
        baseline_characteristics: {
          ...prev.baseline_characteristics,
          measures,
        },
      };
    });
  };

  // Helper to calculate total for Overall Number of Baseline Participants across all arms
  const calculateBaselineParticipantsTotal = () => {
    const arms = formData.baseline_characteristics?.arms_groups || [];
    let total = 0;
    arms.forEach(arm => {
      const value = parseFloat(arm.overall_baseline_participants) || 0;
      total += value;
    });
    return total > 0 ? total.toString() : '';
  };

  // Helper to calculate total for Overall Number of Units Analyzed across all arms
  const calculateBaselineUnitsTotal = () => {
    const arms = formData.baseline_characteristics?.arms_groups || [];
    let total = 0;
    arms.forEach(arm => {
      const value = parseFloat(arm.overall_units_analyzed) || 0;
      total += value;
    });
    return total > 0 ? total.toString() : '';
  };

  // Helper to calculate total for baseline measure data across all arms for a specific row
  const calculateBaselineMeasureDataTotal = (measureIndex, rowIndex) => {
    const measure = formData.baseline_characteristics?.measures?.[measureIndex];
    const row = measure?.rows?.[rowIndex];
    const dataPerArm = row?.data_per_arm || [];
    let total = 0;
    dataPerArm.forEach(armData => {
      const value = parseFloat(armData.baseline_measure_data) || 0;
      total += value;
    });
    return total > 0 ? total.toString() : '';
  };

  const removeBaselineMeasure = (index) => {
    setFormData((prev) => ({
      ...prev,
      baseline_characteristics: {
        ...prev.baseline_characteristics,
        measures: prev.baseline_characteristics.measures.filter(
          (_, i) => i !== index
        ),
      },
    }));
  };

  const updateBaselineMeasure = (index, field, value) => {
    setFormData((prev) => {
      const measures = [...prev.baseline_characteristics.measures];
      measures[index] = { ...measures[index], [field]: value };
      return {
        ...prev,
        baseline_characteristics: {
          ...prev.baseline_characteristics,
          measures,
        },
      };
    });
  };


  const handleSave = async () => {
    if (!projectId) {
      setUploadError('Project ID is missing. Please select a project.');
      return;
    }

    setSaving(true);
    setUploadError(null);

    try {
      const response = await submitResultsService.save(projectId, formData);

      if (response.success) {
        setUploadSuccess('Form saved successfully.');
        // Clear any errors
        setUploadError(null);
      } else {
        setUploadError(response.error || 'Save failed');
      }
    } catch (err) {
      setUploadError(err.response?.data?.error || err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleExportXml = async () => {
    if (!projectId) {
      setUploadError('Project ID is missing. Please select a project.');
      return;
    }

    setExporting(true);
    setUploadError(null);

    try {
      const response = await submitResultsService.exportXml(projectId);

      if (response.success && response.xmlContent) {
        // Create a blob from the XML content
        const blob = new Blob([response.xmlContent], { type: 'application/xml' });
        
        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = response.file.filename || `submit_results_${projectId}.xml`;
        document.body.appendChild(link);
        link.click();
        
        // Cleanup
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        setUploadSuccess('XML exported and downloaded successfully. File saved to database.');
      } else {
        setUploadError(response.error || 'Export failed');
      }
    } catch (err) {
      setUploadError(err.response?.data?.error || err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box sx={{ py: 3, px: 2, minHeight: '60vh', backgroundColor: 'background.paper', borderRadius: '0 0 8px 8px' }}>
      <Grid container spacing={3}>
        {/* Left Side - Form */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 4, height: '100%', overflow: 'auto', maxHeight: 'calc(100vh - 200px)' }}>
            <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
              Scientific Information
            </Typography>

        {/* File Upload Section */}
        <Box
          sx={{
            border: '1.5px solid #90caf9',
            borderRadius: 2,
            p: 3,
            mb: 4,
            backgroundColor: '#f5f5f5',
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            Upload File
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <input
              type="file"
              accept="application/pdf,.doc,.docx"
              onChange={handleFileChange}
              disabled={uploading || isValidatingFile}
              style={{ flex: 1 }}
            />
            <Button
              variant="contained"
              startIcon={<UploadIcon />}
              onClick={handleFileUpload}
              disabled={uploading || !uploadedFile || isValidatingFile || (fileValidationStatus && !fileValidationStatus.isValid)}
            >
              {uploading ? <CircularProgress size={20} /> : isValidatingFile ? <CircularProgress size={20} /> : 'Upload & Extract'}
            </Button>
          </Stack>
          
          {/* File Validation Status */}
          {isValidatingFile && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Validating file for readable text...
            </Alert>
          )}
          
          {uploadedFile && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Selected: {uploadedFile.name}
            </Typography>
          )}
          
          {fileValidationStatus && !isValidatingFile && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 1,
                border: `2px solid ${fileValidationStatus.isValid ? '#4caf50' : '#f44336'}`,
                backgroundColor: fileValidationStatus.isValid ? '#e8f5e9' : '#ffebee',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              {fileValidationStatus.isValid ? (
                <>
                  <CheckCircleIcon sx={{ color: '#4caf50' }} />
                  <Box>
                    <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 600 }}>
                      ✓ File is readable
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#2e7d32', display: 'block' }}>
                      {fileValidationStatus.reason}
                    </Typography>
                  </Box>
                </>
              ) : (
                <>
                  <ErrorOutlineIcon sx={{ color: '#f44336' }} />
                  <Box>
                    <Typography variant="body2" sx={{ color: '#c62828', fontWeight: 600 }}>
                      ✗ File validation failed
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#c62828', display: 'block' }}>
                      {fileValidationStatus.reason}
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
          )}
          
          {uploadError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {uploadError}
            </Alert>
          )}
          {uploadSuccess && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {uploadSuccess}
            </Alert>
          )}
          {fileInfo && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Source: {fileInfo.originalName || 'Uploaded file'}
            </Typography>
          )}
        </Box>

        {/* Participant Flow Section */}
        <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 3 }}>
          Participant Flow
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Information to document the progress of research participants through each stage of a study in a tabular format, including the number of participants who started and completed the clinical study.
        </Typography>

        {/* Recruitment Details */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12}>
            <TextField
              label="Recruitment Details *"
              value={formData.recruitment_details}
              onChange={(e) => handleInputChange('recruitment_details', e.target.value)}
              onFocus={() => {
                const meta = getFieldMetadata('recruitment_details');
                if (meta && meta.page && pdfViewerRef.current) {
                  pdfViewerRef.current.navigateToPage(meta.page);
                }
              }}
              fullWidth
              multiline
              rows={3}
              sx={{
                '& .MuiInputBase-root': {
                  cursor: getFieldMetadata('recruitment_details') ? 'pointer' : 'text',
                }
              }}
              helperText={
                getFieldMetadata('recruitment_details') 
                  ? `📄 Focus to view source on page ${getFieldMetadata('recruitment_details').page} | ${(formData.recruitment_details || '').length}/500 characters`
                  : `${(formData.recruitment_details || '').length}/500 characters`
              }
              inputProps={{ maxLength: 500 }}
              disabled={loading || saving}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              Key information relevant to the recruitment process for the overall study, such as dates of the recruitment period and types of location (For example, medical clinic), to provide context.
            </Typography>
          </Grid>
        </Grid>

        {/* Pre-assignment Details */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12}>
            <TextField
              label="Pre-assignment Details"
              value={formData.pre_assignment_details}
              onChange={(e) => handleInputChange('pre_assignment_details', e.target.value)}
              onFocus={() => handleFieldClick('pre_assignment_details')}
              fullWidth
              multiline
              rows={3}
              sx={{
                '& .MuiInputBase-root': {
                  cursor: hasFieldSource('pre_assignment_details') ? 'pointer' : 'text',
                }
              }}
              inputProps={{ maxLength: 500 }}
              helperText={
                hasFieldSource('pre_assignment_details')
                  ? `📄 Focus to view source on page ${getFieldMetadata('pre_assignment_details').page} | ${(formData.pre_assignment_details || '').length}/500 characters`
                  : `${(formData.pre_assignment_details || '').length}/500 characters`
              }
              disabled={loading || saving}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              Description of significant events in the study (for example, wash out, run-in) that occur after participant enrollment, but prior to assignment of participants to an arm or group, if any.
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        {/* Arm/Group Information */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Arm/Group Information *
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Arms or groups for describing the flow of participants through the clinical study. In general, it must include each arm to which participants were assigned.
          </Typography>

          {(formData.arms_groups || []).map((arm, index) => (
            <Paper key={index} elevation={1} sx={{ p: 3, mb: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="subtitle1">Arm/Group {index + 1}</Typography>
                {(formData.arms_groups?.length || 0) > 1 && (
                  <IconButton
                    color="error"
                    onClick={() => removeArmGroup(index)}
                    disabled={loading || saving}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Stack>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Arm/Group Title *"
                    value={arm.title || ''}
                    onChange={(e) => handleArrayChange('arms_groups', index, 'title', e.target.value)}
                    onFocus={() => handleFieldClick(getNestedFieldPath('arms_groups', index, 'title'))}
                    fullWidth
                    sx={{
                      '& .MuiInputBase-root': {
                        cursor: hasFieldSource(getNestedFieldPath('arms_groups', index, 'title')) ? 'pointer' : 'text',
                      }
                    }}
                    inputProps={{ minLength: 4, maxLength: 100 }}
                    helperText={
                      hasFieldSource(getNestedFieldPath('arms_groups', index, 'title'))
                        ? `📄 Focus to view source on page ${getFieldMetadata(getNestedFieldPath('arms_groups', index, 'title'))?.page} | ${(arm.title || '').length} characters (4-100 required)`
                        : `${(arm.title || '').length} characters (4-100 required)`
                    }
                    disabled={loading || saving}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Arm/Group Description *"
                    value={arm.description || ''}
                    onChange={(e) => handleArrayChange('arms_groups', index, 'description', e.target.value)}
                    onFocus={() => handleFieldClick(getNestedFieldPath('arms_groups', index, 'description'))}
                    fullWidth
                    multiline
                    rows={4}
                    sx={{
                      '& .MuiInputBase-root': {
                        cursor: hasFieldSource(getNestedFieldPath('arms_groups', index, 'description')) ? 'pointer' : 'text',
                      }
                    }}
                    inputProps={{ maxLength: 1500 }}
                    helperText={
                      hasFieldSource(getNestedFieldPath('arms_groups', index, 'description'))
                        ? `📄 Focus to view source on page ${getFieldMetadata(getNestedFieldPath('arms_groups', index, 'description'))?.page} | ${(arm.description || '').length}/1500 characters`
                        : `${(arm.description || '').length}/1500 characters`
                    }
                    disabled={loading || saving}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Type of Units Assigned"
                    value={arm.type_of_units_assigned || ''}
                    onChange={(e) => handleArrayChange('arms_groups', index, 'type_of_units_assigned', e.target.value)}
                    onFocus={() => handleFieldClick(getNestedFieldPath('arms_groups', index, 'type_of_units_assigned'))}
                    fullWidth
                    sx={{
                      '& .MuiInputBase-root': {
                        cursor: hasFieldSource(getNestedFieldPath('arms_groups', index, 'type_of_units_assigned')) ? 'pointer' : 'text',
                      }
                    }}
                    inputProps={{ maxLength: 40 }}
                    helperText={
                      hasFieldSource(getNestedFieldPath('arms_groups', index, 'type_of_units_assigned'))
                        ? `📄 Focus to view source on page ${getFieldMetadata(getNestedFieldPath('arms_groups', index, 'type_of_units_assigned'))?.page} | ${(arm.type_of_units_assigned || '').length}/40 characters`
                        : `${(arm.type_of_units_assigned || '').length}/40 characters`
                    }
                    disabled={loading || saving}
                    placeholder="e.g., eyes, lesions, implants"
                  />
                </Grid>
              </Grid>
            </Paper>
          ))}

          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addArmGroup}
            disabled={loading || saving}
            sx={{ mt: 2 }}
          >
            Add Arm/Group
          </Button>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Period(s) */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Period(s) *
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Discrete stages of a clinical study during which numbers of participants at specific significant events or points of time are reported.
          </Typography>

          {(formData.periods || []).map((period, periodIndex) => (
            <Paper key={periodIndex} elevation={1} sx={{ p: 3, mb: 3 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="subtitle1">Period {periodIndex + 1}</Typography>
                {formData.periods.length > 1 && (
                  <IconButton
                    color="error"
                    onClick={() => removePeriod(periodIndex)}
                    disabled={loading || saving}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Stack>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Period Title *"
                    value={period.title}
                    onChange={(e) => handleArrayChange('periods', periodIndex, 'title', e.target.value)}
                    onFocus={() => handleFieldClick(getNestedFieldPath('periods', periodIndex, 'title'))}
                    fullWidth
                    sx={{
                      '& .MuiInputBase-root': {
                        cursor: hasFieldSource(getNestedFieldPath('periods', periodIndex, 'title')) ? 'pointer' : 'text',
                      }
                    }}
                    inputProps={{ maxLength: 40 }}
                    helperText={
                      hasFieldSource(getNestedFieldPath('periods', periodIndex, 'title'))
                        ? `📄 Focus to view source on page ${getFieldMetadata(getNestedFieldPath('periods', periodIndex, 'title'))?.page} | ${(period.title || '').length}/40 characters`
                        : `${(period.title || '').length}/40 characters`
                    }
                    disabled={loading || saving}
                    placeholder={formData.periods.length === 1 ? 'Overall Study' : 'Enter period title'}
                  />
                </Grid>

                {/* Milestones Section */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mt: 2, mb: 2, fontWeight: 600 }}>
                    Milestones *
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Each period consists of milestones for reporting numbers of participants at particular points in time. Started and Completed are required milestones.
                  </Typography>
                  
                  {period.milestones?.map((milestone, milestoneIndex) => {
                    const isRequired = milestone.is_required;
                    return (
                      <Paper 
                        key={milestoneIndex} 
                        elevation={0} 
                        sx={{ 
                          p: 2, 
                          mb: 2, 
                          bgcolor: '#f9f9f9',
                          border: '1px solid #e0e0e0'
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2" fontWeight={isRequired ? 600 : 400}>
                              {milestone.title} {isRequired && '(Required)'}
                            </Typography>
                          </Stack>
                          {!isRequired && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => removeMilestone(periodIndex, milestoneIndex)}
                              disabled={loading || saving}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Stack>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={isRequired ? 6 : 4}>
                            <TextField
                              label="Milestone Title"
                              value={milestone.title}
                              onChange={(e) => {
                                const newData = { ...formData };
                                newData.periods[periodIndex].milestones[milestoneIndex].title = e.target.value;
                                setFormData(newData);
                              }}
                              onFocus={() => handleFieldClick(getDeepNestedFieldPath('periods', periodIndex, 'milestones', milestoneIndex, 'title'))}
                              fullWidth
                              sx={{
                                '& .MuiInputBase-root': {
                                  cursor: hasFieldSource(getDeepNestedFieldPath('periods', periodIndex, 'milestones', milestoneIndex, 'title')) ? 'pointer' : 'text',
                                }
                              }}
                              inputProps={{ maxLength: 100 }}
                              helperText={
                                hasFieldSource(getDeepNestedFieldPath('periods', periodIndex, 'milestones', milestoneIndex, 'title'))
                                  ? `📄 Focus to view source on page ${getFieldMetadata(getDeepNestedFieldPath('periods', periodIndex, 'milestones', milestoneIndex, 'title'))?.page} | ${(milestone.title || '').length}/100 characters`
                                  : `${(milestone.title || '').length}/100 characters`
                              }
                              disabled={loading || saving || isRequired}
                              required={isRequired}
                            />
                          </Grid>
                          <Grid item xs={12} md={isRequired ? 6 : 4}>
                            <TextField
                              label="Milestone Data *"
                              type="number"
                            value={milestone.data ?? ''}
                              onChange={(e) => {
                                const newData = { ...formData };
                                newData.periods[periodIndex].milestones[milestoneIndex].data = sanitizeNumericValue(e.target.value);
                                setFormData(newData);
                              }}
                              onFocus={() => handleFieldClick(getDeepNestedFieldPath('periods', periodIndex, 'milestones', milestoneIndex, 'data'))}
                              fullWidth
                              sx={{
                                '& .MuiInputBase-root': {
                                  cursor: hasFieldSource(getDeepNestedFieldPath('periods', periodIndex, 'milestones', milestoneIndex, 'data')) ? 'pointer' : 'text',
                                }
                              }}
                              disabled={loading || saving}
                              required
                              helperText={
                                hasFieldSource(getDeepNestedFieldPath('periods', periodIndex, 'milestones', milestoneIndex, 'data'))
                                  ? `📄 Focus to view source on page ${getFieldMetadata(getDeepNestedFieldPath('periods', periodIndex, 'milestones', milestoneIndex, 'data'))?.page} | ${isRequired 
                                    ? (milestone.title === 'Started' 
                                        ? 'Number of participants initiating the period' 
                                        : 'Number of participants at the end of the period')
                                    : 'Number of participants to reach the milestone'}`
                                  : (isRequired 
                                    ? (milestone.title === 'Started' 
                                        ? 'Number of participants initiating the period' 
                                        : 'Number of participants at the end of the period')
                                    : 'Number of participants to reach the milestone')
                              }
                            />
                          </Grid>
                          {!isRequired && (
                            <Grid item xs={12} md={4}>
                              <TextField
                                label="Comments"
                            value={milestone.comments || ''}
                                onChange={(e) => {
                                  const newData = { ...formData };
                                  newData.periods[periodIndex].milestones[milestoneIndex].comments = e.target.value;
                                  setFormData(newData);
                                }}
                                onFocus={() => handleFieldClick(getDeepNestedFieldPath('periods', periodIndex, 'milestones', milestoneIndex, 'comments'))}
                                fullWidth
                                multiline
                                rows={2}
                                sx={{
                                  '& .MuiInputBase-root': {
                                    cursor: hasFieldSource(getDeepNestedFieldPath('periods', periodIndex, 'milestones', milestoneIndex, 'comments')) ? 'pointer' : 'text',
                                  }
                                }}
                                inputProps={{ maxLength: 500 }}
                                helperText={
                                  hasFieldSource(getDeepNestedFieldPath('periods', periodIndex, 'milestones', milestoneIndex, 'comments'))
                                    ? `📄 Focus to view source on page ${getFieldMetadata(getDeepNestedFieldPath('periods', periodIndex, 'milestones', milestoneIndex, 'comments'))?.page} | ${(milestone.comments || '').length}/500 characters`
                                    : `${(milestone.comments || '').length}/500 characters`
                                }
                                disabled={loading || saving}
                              />
                            </Grid>
                          )}
                          {isRequired && (
                            <Grid item xs={12}>
                              <TextField
                                label="Comments"
                            value={milestone.comments || ''}
                                onChange={(e) => {
                                  const newData = { ...formData };
                                  newData.periods[periodIndex].milestones[milestoneIndex].comments = e.target.value;
                                  setFormData(newData);
                                }}
                                onFocus={() => handleFieldClick(getDeepNestedFieldPath('periods', periodIndex, 'milestones', milestoneIndex, 'comments'))}
                                fullWidth
                                multiline
                                rows={2}
                                sx={{
                                  '& .MuiInputBase-root': {
                                    cursor: hasFieldSource(getDeepNestedFieldPath('periods', periodIndex, 'milestones', milestoneIndex, 'comments')) ? 'pointer' : 'text',
                                  }
                                }}
                                inputProps={{ maxLength: 500 }}
                                helperText={
                                  hasFieldSource(getDeepNestedFieldPath('periods', periodIndex, 'milestones', milestoneIndex, 'comments'))
                                    ? `📄 Focus to view source on page ${getFieldMetadata(getDeepNestedFieldPath('periods', periodIndex, 'milestones', milestoneIndex, 'comments'))?.page} | ${(milestone.comments || '').length}/500 characters`
                                    : `${(milestone.comments || '').length}/500 characters`
                                }
                                disabled={loading || saving}
                              />
                            </Grid>
                          )}
                        </Grid>
                      </Paper>
                    );
                  })}
                  
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => addMilestone(periodIndex)}
                    disabled={loading || saving}
                    sx={{ mt: 1 }}
                  >
                    Add Additional Milestone
                  </Button>
                </Grid>

                {/* Not Completed (Calculated) */}
                <Grid item xs={12}>
                  <TextField
                    label="Not Completed (Calculated Automatically)"
                    type="number"
                    value={calculateNotCompleted(periodIndex)}
                    fullWidth
                    disabled
                    helperText="Automatically calculated: Started - Completed"
                  />
                </Grid>

                {/* Reason Not Completed */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                    Reason Not Completed
                  </Typography>
                  {period.reasons_not_completed?.map((reason, reasonIndex) => (
                    <Paper key={reasonIndex} elevation={0} sx={{ p: 2, mb: 2, bgcolor: '#f9f9f9' }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="body2">Reason {reasonIndex + 1}</Typography>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeReasonNotCompleted(periodIndex, reasonIndex)}
                          disabled={loading || saving}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                          <TextField
                            select
                            label="Reason Not Completed Type"
                            value={reason.type || ''}
                            onChange={(e) => {
                              const newData = { ...formData };
                              newData.periods[periodIndex].reasons_not_completed[reasonIndex].type = e.target.value;
                              if (e.target.value !== 'Other') {
                                newData.periods[periodIndex].reasons_not_completed[reasonIndex].other_reason = '';
                              }
                              setFormData(newData);
                            }}
                            fullWidth
                            disabled={loading || saving}
                          >
                            {reasonNotCompletedOptions.map((option) => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        {reason.type === 'Other' && (
                          <Grid item xs={12} md={4}>
                            <TextField
                              label="Other Reason *"
                            value={reason.other_reason || ''}
                              onChange={(e) => {
                                const newData = { ...formData };
                                newData.periods[periodIndex].reasons_not_completed[reasonIndex].other_reason = e.target.value;
                                setFormData(newData);
                              }}
                              onFocus={() => handleFieldClick(`periods[${periodIndex}].reasons_not_completed[${reasonIndex}].other_reason`)}
                              fullWidth
                              sx={{
                                '& .MuiInputBase-root': {
                                  cursor: hasFieldSource(`periods[${periodIndex}].reasons_not_completed[${reasonIndex}].other_reason`) ? 'pointer' : 'text',
                                }
                              }}
                              inputProps={{ maxLength: 100 }}
                              helperText={
                                hasFieldSource(`periods[${periodIndex}].reasons_not_completed[${reasonIndex}].other_reason`)
                                  ? `📄 Focus to view source on page ${getFieldMetadata(`periods[${periodIndex}].reasons_not_completed[${reasonIndex}].other_reason`)?.page} | ${(reason.other_reason || '').length}/100 characters`
                                  : `${(reason.other_reason || '').length}/100 characters`
                              }
                              disabled={loading || saving}
                            />
                          </Grid>
                        )}
                        <Grid item xs={12} md={reason.type === 'Other' ? 4 : 8}>
                          <TextField
                            label="Reason Not Completed Data"
                            type="number"
                            value={reason.data ?? ''}
                            onChange={(e) => {
                              const newData = { ...formData };
                              newData.periods[periodIndex].reasons_not_completed[reasonIndex].data = sanitizeNumericValue(e.target.value);
                              setFormData(newData);
                            }}
                            onFocus={() => handleFieldClick(`periods[${periodIndex}].reasons_not_completed[${reasonIndex}].data`)}
                            fullWidth
                            sx={{
                              '& .MuiInputBase-root': {
                                cursor: hasFieldSource(`periods[${periodIndex}].reasons_not_completed[${reasonIndex}].data`) ? 'pointer' : 'text',
                              }
                            }}
                            disabled={loading || saving}
                            helperText={
                              hasFieldSource(`periods[${periodIndex}].reasons_not_completed[${reasonIndex}].data`)
                                ? `📄 Focus to view source on page ${getFieldMetadata(`periods[${periodIndex}].reasons_not_completed[${reasonIndex}].data`)?.page} | Number of participants in each arm or group`
                                : 'Number of participants in each arm or group'
                            }
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => addReasonNotCompleted(periodIndex)}
                    disabled={loading || saving}
                    sx={{ mt: 1 }}
                  >
                    Add Reason Not Completed
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          ))}

          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addPeriod}
            disabled={loading || saving}
            sx={{ mt: 2 }}
          >
            Add Period
          </Button>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Baseline Characteristics Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
            Baseline Characteristics
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Demographic and baseline measures collected by arm/group and for the entire study population.
          </Typography>

          {/* Baseline Arm/Group Information */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Baseline Arm/Group Information *
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Arms or comparison groups in the study, including all participants assessed at baseline as specified in the protocol or statistical analysis plan.
            </Typography>

            {(formData.baseline_characteristics?.arms_groups || []).map((arm, index) => (
              <Paper key={index} elevation={1} sx={{ p: 3, mb: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="subtitle1">Baseline Arm/Group {index + 1}</Typography>
                  {(formData.baseline_characteristics?.arms_groups?.length || 0) > 1 && (
                    <IconButton
                      color="error"
                      onClick={() => removeBaselineArmGroup(index)}
                      disabled={loading || saving}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Stack>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Arm/Group Title *"
                      value={arm.title}
                      onChange={(e) => updateBaselineArmGroup(index, 'title', e.target.value)}
                      onFocus={() => handleFieldClick(`baseline_characteristics.arms_groups[${index}].title`)}
                      fullWidth
                      sx={{
                        '& .MuiInputBase-root': {
                          cursor: hasFieldSource(`baseline_characteristics.arms_groups[${index}].title`) ? 'pointer' : 'text',
                        }
                      }}
                      inputProps={{ minLength: 4, maxLength: 100 }}
                      helperText={
                        hasFieldSource(`baseline_characteristics.arms_groups[${index}].title`)
                          ? `📄 Focus to view source on page ${getFieldMetadata(`baseline_characteristics.arms_groups[${index}].title`)?.page} | ${arm.title?.length || 0} characters (4-100 required)`
                          : `${arm.title?.length || 0} characters (4-100 required)`
                      }
                      disabled={loading || saving}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Arm/Group Description *"
                      value={arm.description}
                      onChange={(e) => updateBaselineArmGroup(index, 'description', e.target.value)}
                      onFocus={() => handleFieldClick(`baseline_characteristics.arms_groups[${index}].description`)}
                      fullWidth
                      multiline
                      rows={4}
                      sx={{
                        '& .MuiInputBase-root': {
                          cursor: hasFieldSource(`baseline_characteristics.arms_groups[${index}].description`) ? 'pointer' : 'text',
                        }
                      }}
                      inputProps={{ maxLength: 1500 }}
                      helperText={
                        hasFieldSource(`baseline_characteristics.arms_groups[${index}].description`)
                          ? `📄 Focus to view source on page ${getFieldMetadata(`baseline_characteristics.arms_groups[${index}].description`)?.page} | ${arm.description?.length || 0}/1500 characters`
                          : `${arm.description?.length || 0}/1500 characters`
                      }
                      disabled={loading || saving}
                    />
                  </Grid>
                  {/* Analysis Population per arm/group */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                      Analysis Population for this Arm/Group
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Overall Number of Baseline Participants *"
                      value={arm.overall_baseline_participants || ''}
                      onChange={(e) => updateBaselineArmGroup(index, 'overall_baseline_participants', e.target.value)}
                      onFocus={() => handleFieldClick(`baseline_characteristics.arms_groups[${index}].overall_baseline_participants`)}
                      fullWidth
                      required
                      sx={{
                        '& .MuiInputBase-root': {
                          cursor: hasFieldSource(`baseline_characteristics.arms_groups[${index}].overall_baseline_participants`) ? 'pointer' : 'text',
                        }
                      }}
                      disabled={loading || saving}
                      helperText={
                        hasFieldSource(`baseline_characteristics.arms_groups[${index}].overall_baseline_participants`)
                          ? `📄 Focus to view source on page ${getFieldMetadata(`baseline_characteristics.arms_groups[${index}].overall_baseline_participants`)?.page} | Number of participants for this arm/group`
                          : 'Number of participants for this arm/group'
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Overall Number of Units Analyzed"
                      value={arm.overall_units_analyzed || ''}
                      onChange={(e) => updateBaselineArmGroup(index, 'overall_units_analyzed', e.target.value)}
                      onFocus={() => handleFieldClick(`baseline_characteristics.arms_groups[${index}].overall_units_analyzed`)}
                      fullWidth
                      sx={{
                        '& .MuiInputBase-root': {
                          cursor: hasFieldSource(`baseline_characteristics.arms_groups[${index}].overall_units_analyzed`) ? 'pointer' : 'text',
                        }
                      }}
                      disabled={loading || saving}
                      helperText={
                        hasFieldSource(`baseline_characteristics.arms_groups[${index}].overall_units_analyzed`)
                          ? `📄 Focus to view source on page ${getFieldMetadata(`baseline_characteristics.arms_groups[${index}].overall_units_analyzed`)?.page} | If analysis is based on units other than participants`
                          : 'If analysis is based on units other than participants'
                      }
                    />
                  </Grid>
                </Grid>
              </Paper>
            ))}

            {/* Total Row */}
            {(formData.baseline_characteristics?.arms_groups?.length || 0) > 0 && (
              <Paper elevation={2} sx={{ p: 2, mb: 2, bgcolor: '#e3f2fd', border: '2px solid #1976d2' }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: '#1976d2' }}>
                  Total (Sum of All Arms/Groups)
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Total: Overall Number of Baseline Participants"
                      value={calculateBaselineParticipantsTotal()}
                      fullWidth
                      disabled
                      InputProps={{
                        readOnly: true,
                      }}
                      helperText="Automatically calculated sum of all arms/groups"
                      sx={{
                        '& .MuiInputBase-root': {
                          backgroundColor: '#ffffff',
                          fontWeight: 600,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Total: Overall Number of Units Analyzed"
                      value={calculateBaselineUnitsTotal()}
                      fullWidth
                      disabled
                      InputProps={{
                        readOnly: true,
                      }}
                      helperText="Automatically calculated sum of all arms/groups"
                      sx={{
                        '& .MuiInputBase-root': {
                          backgroundColor: '#ffffff',
                          fontWeight: 600,
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            )}

            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={addBaselineArmGroup}
              disabled={loading || saving}
              sx={{ mt: 2 }}
            >
              Add Baseline Arm/Group
            </Button>
          </Box>

          {/* Shared Baseline Analysis Population Information */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Shared Baseline Analysis Population Information
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Type of Units Analyzed"
                  value={formData.baseline_characteristics?.analysis_population?.type_of_units_analyzed || ''}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      baseline_characteristics: {
                        ...(prev.baseline_characteristics || {}),
                        analysis_population: {
                          ...(prev.baseline_characteristics?.analysis_population || {}),
                          type_of_units_analyzed: e.target.value,
                        },
                      },
                    }))
                  }
                  onFocus={() => handleFieldClick('baseline_characteristics.analysis_population.type_of_units_analyzed')}
                  fullWidth
                  sx={{
                    '& .MuiInputBase-root': {
                      cursor: hasFieldSource('baseline_characteristics.analysis_population.type_of_units_analyzed') ? 'pointer' : 'text',
                    }
                  }}
                  inputProps={{ maxLength: 40 }}
                  helperText={
                    hasFieldSource('baseline_characteristics.analysis_population.type_of_units_analyzed')
                      ? `📄 Focus to view source on page ${getFieldMetadata('baseline_characteristics.analysis_population.type_of_units_analyzed')?.page} | ${(formData.baseline_characteristics?.analysis_population?.type_of_units_analyzed || '').length}/40 characters`
                      : `${(formData.baseline_characteristics?.analysis_population?.type_of_units_analyzed || '').length}/40 characters`
                  }
                  disabled={loading || saving}
                  placeholder="e.g., eyes, lesions, implants"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Baseline Analysis Population Description"
                  value={formData.baseline_characteristics?.analysis_population?.analysis_population_description || ''}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      baseline_characteristics: {
                        ...(prev.baseline_characteristics || {}),
                        analysis_population: {
                          ...(prev.baseline_characteristics?.analysis_population || {}),
                          analysis_population_description: e.target.value,
                        },
                      },
                    }))
                  }
                  onFocus={() => handleFieldClick('baseline_characteristics.analysis_population.analysis_population_description')}
                  sx={{
                    '& .MuiInputBase-root': {
                      cursor: hasFieldSource('baseline_characteristics.analysis_population.analysis_population_description') ? 'pointer' : 'text',
                    }
                  }}
                  fullWidth
                  multiline
                  rows={3}
                  inputProps={{ maxLength: 500 }}
                  helperText={
                    hasFieldSource('baseline_characteristics.analysis_population.analysis_population_description')
                      ? `📄 Focus to view source on page ${getFieldMetadata('baseline_characteristics.analysis_population.analysis_population_description')?.page} | ${(formData.baseline_characteristics?.analysis_population?.analysis_population_description || '').length}/500 characters`
                      : `${(formData.baseline_characteristics?.analysis_population?.analysis_population_description || '').length}/500 characters`
                  }
                  disabled={loading || saving}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Baseline Measures */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Baseline Measures *
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Describe each baseline or demographic characteristic measured (for example, Age, Sex/Gender, Race, Ethnicity, and study-specific measures).
            </Typography>

            {(formData.baseline_characteristics?.measures || []).map((measure, index) => (
              <Paper key={index} elevation={1} sx={{ p: 3, mb: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="subtitle1">Baseline Measure {index + 1}</Typography>
                  {(formData.baseline_characteristics?.measures?.length || 0) > 1 && (
                    <IconButton
                      color="error"
                      onClick={() => removeBaselineMeasure(index)}
                      disabled={loading || saving}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Stack>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      select
                      label="Baseline Measure Title *"
                      value={measure.measure_title || ''}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        updateBaselineMeasure(index, 'measure_title', newValue);
                        // Reset sub_type when main title changes
                        updateBaselineMeasure(index, 'measure_sub_type', '');
                        // Clear study_specific_title if not study-specific
                        if (newValue !== 'Study-Specific Measure') {
                          updateBaselineMeasure(index, 'study_specific_title', '');
                        }
                        // Reset measure_type and unit_of_measure when title changes
                        updateBaselineMeasure(index, 'measure_type', '');
                        updateBaselineMeasure(index, 'unit_of_measure', '');
                        updateBaselineMeasure(index, 'dispersion_type', '');
                      }}
                      fullWidth
                      disabled={loading || saving}
                      required
                    >
                      {baselineMeasureTitleOptions.map((opt) => (
                        <MenuItem key={opt} value={opt}>
                          {opt}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  
                  {/* Show sub-options based on main selection */}
                  {measure.measure_title === 'Age' && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        select
                        label="Age Type * (Select at least one)"
                        value={measure.measure_sub_type || ''}
                        onChange={(e) => {
                          const newSubType = e.target.value;
                          updateBaselineMeasure(index, 'measure_sub_type', newSubType);
                          // Auto-set defaults based on Age sub-type
                          if (measure.measure_title === 'Age') {
                            if (newSubType === 'Age, Categorical') {
                              // Auto-set Measure Type to Count of Participants and Unit to Participants
                              updateBaselineMeasure(index, 'measure_type', 'Count of Participants');
                              updateBaselineMeasure(index, 'unit_of_measure', 'Participants');
                              updateBaselineMeasure(index, 'dispersion_type', 'Not Applicable');
                              // Auto-create the 3 predefined categorical rows
                              const categoricalRows = ageCategoricalOptions.map(cat => ({
                                category_or_row_title: cat,
                                baseline_measure_data: '',
                                na_explanation: '',
                              }));
                              setFormData((prev) => {
                                const measures = [...prev.baseline_characteristics.measures];
                                measures[index] = { ...measures[index], rows: categoricalRows };
                                return {
                                  ...prev,
                                  baseline_characteristics: {
                                    ...prev.baseline_characteristics,
                                    measures,
                                  },
                                };
                              });
                            } else if (newSubType === 'Age, Continuous') {
                              // Clear defaults for Continuous
                              updateBaselineMeasure(index, 'measure_type', '');
                              updateBaselineMeasure(index, 'unit_of_measure', '');
                              updateBaselineMeasure(index, 'dispersion_type', '');
                              // For Continuous, typically one row (empty)
                              setFormData((prev) => {
                                const measures = [...prev.baseline_characteristics.measures];
                                measures[index] = {
                                  ...measures[index],
                                  rows: [{
                                    category_or_row_title: '',
                                    baseline_measure_data: '',
                                    na_explanation: '',
                                  }],
                                };
                                return {
                                  ...prev,
                                  baseline_characteristics: {
                                    ...prev.baseline_characteristics,
                                    measures,
                                  },
                                };
                              });
                            } else if (newSubType === 'Age, Customized') {
                              // Clear defaults for Customized
                              updateBaselineMeasure(index, 'measure_type', '');
                              updateBaselineMeasure(index, 'unit_of_measure', '');
                              updateBaselineMeasure(index, 'dispersion_type', '');
                              // For Customized, start with one empty row (user can add more)
                              setFormData((prev) => {
                                const measures = [...prev.baseline_characteristics.measures];
                                measures[index] = {
                                  ...measures[index],
                                  rows: [{
                                    category_or_row_title: '',
                                    baseline_measure_data: '',
                                    na_explanation: '',
                                  }],
                                };
                                return {
                                  ...prev,
                                  baseline_characteristics: {
                                    ...prev.baseline_characteristics,
                                    measures,
                                  },
                                };
                              });
                            }
                          }
                        }}
                        fullWidth
                        disabled={loading || saving}
                        required
                      >
                        {ageSubOptions.map((opt) => (
                          <MenuItem key={opt} value={opt}>
                            {opt}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  )}

                  {measure.measure_title === 'Sex/Gender' && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        select
                        label="Sex/Gender Type * (Select at least one)"
                        value={measure.measure_sub_type || ''}
                        onChange={(e) => {
                          const newSubType = e.target.value;
                          updateBaselineMeasure(index, 'measure_sub_type', newSubType);
                          // Auto-create rows based on selection
                          if (newSubType === 'Sex: Female, Male') {
                            const sexGenderRows = sexGenderFemaleMaleOptions.map(opt => ({
                              category_or_row_title: opt,
                              baseline_measure_data: '',
                              na_explanation: '',
                            }));
                            setFormData((prev) => {
                              const measures = [...prev.baseline_characteristics.measures];
                              measures[index] = { ...measures[index], rows: sexGenderRows };
                              return {
                                ...prev,
                                baseline_characteristics: {
                                  ...prev.baseline_characteristics,
                                  measures,
                                },
                              };
                            });
                          } else {
                            // For Customized, start with one empty row
                            setFormData((prev) => {
                              const measures = [...prev.baseline_characteristics.measures];
                              measures[index] = {
                                ...measures[index],
                                rows: [{
                                  category_or_row_title: '',
                                  baseline_measure_data: '',
                                  na_explanation: '',
                                }],
                              };
                              return {
                                ...prev,
                                baseline_characteristics: {
                                  ...prev.baseline_characteristics,
                                  measures,
                                },
                              };
                            });
                          }
                        }}
                        fullWidth
                        disabled={loading || saving}
                        required
                      >
                        {sexGenderSubOptions.map((opt) => (
                          <MenuItem key={opt} value={opt}>
                            {opt}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  )}

                  {measure.measure_title === 'Race and Ethnicity' && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        select
                        label="Race and Ethnicity Type *"
                        value={measure.measure_sub_type || ''}
                        onChange={(e) => {
                          const newSubType = e.target.value;
                          updateBaselineMeasure(index, 'measure_sub_type', newSubType);
                          // Auto-create rows based on selection
                          if (newSubType === 'Race (NIH/OMB)') {
                            const raceRows = raceNIHOMBOptions.map(opt => ({
                              category_or_row_title: opt,
                              baseline_measure_data: '',
                              na_explanation: '',
                            }));
                            setFormData((prev) => {
                              const measures = [...prev.baseline_characteristics.measures];
                              measures[index] = { ...measures[index], rows: raceRows };
                              return {
                                ...prev,
                                baseline_characteristics: {
                                  ...prev.baseline_characteristics,
                                  measures,
                                },
                              };
                            });
                          } else if (newSubType === 'Ethnicity (NIH/OMB)') {
                            const ethnicityRows = ethnicityNIHOMBOptions.map(opt => ({
                              category_or_row_title: opt,
                              baseline_measure_data: '',
                              na_explanation: '',
                            }));
                            setFormData((prev) => {
                              const measures = [...prev.baseline_characteristics.measures];
                              measures[index] = { ...measures[index], rows: ethnicityRows };
                              return {
                                ...prev,
                                baseline_characteristics: {
                                  ...prev.baseline_characteristics,
                                  measures,
                                },
                              };
                            });
                          } else {
                            // For Customized or Not Collected, start with one empty row
                            setFormData((prev) => {
                              const measures = [...prev.baseline_characteristics.measures];
                              measures[index] = {
                                ...measures[index],
                                rows: [{
                                  category_or_row_title: '',
                                  baseline_measure_data: '',
                                  na_explanation: '',
                                }],
                              };
                              return {
                                ...prev,
                                baseline_characteristics: {
                                  ...prev.baseline_characteristics,
                                  measures,
                                },
                              };
                            });
                          }
                        }}
                        fullWidth
                        disabled={loading || saving}
                        required
                      >
                        {raceEthnicitySubOptions.map((opt) => (
                          <MenuItem key={opt} value={opt}>
                            {opt}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  )}

                  {/* Study-Specific Baseline Measure Title - comes right after sub-type selection, before Description */}
                  {measure.measure_title === 'Study-Specific Measure' && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Study-Specific Baseline Measure Title(s) *"
                        value={measure.study_specific_title || ''}
                        onChange={(e) => updateBaselineMeasure(index, 'study_specific_title', e.target.value)}
                        onFocus={() => handleFieldClick(`baseline_characteristics.measures[${index}].study_specific_title`)}
                        fullWidth
                        sx={{
                          '& .MuiInputBase-root': {
                            cursor: hasFieldSource(`baseline_characteristics.measures[${index}].study_specific_title`) ? 'pointer' : 'text',
                          }
                        }}
                        inputProps={{ maxLength: 100 }}
                        helperText={
                          hasFieldSource(`baseline_characteristics.measures[${index}].study_specific_title`)
                            ? `📄 Focus to view source on page ${getFieldMetadata(`baseline_characteristics.measures[${index}].study_specific_title`)?.page} | ${measure.study_specific_title?.length || 0}/100 characters. If "Study-Specific Measure" is chosen, provide the name of the measure.`
                            : `${measure.study_specific_title?.length || 0}/100 characters. If "Study-Specific Measure" is chosen, provide the name of the measure.`
                        }
                        disabled={loading || saving}
                        required
                      />
                    </Grid>
                  )}

                  {/* Baseline Measure Description - comes after Study-Specific Title (if applicable) */}
                  {/* Only show for Study-Specific Measure and other non-standard measures */}
                  {(measure.measure_title === 'Study-Specific Measure' || !['Age', 'Sex/Gender', 'Race and Ethnicity', 'Region of Enrollment'].includes(measure.measure_title)) && (
                    <Grid item xs={12}>
                      <TextField
                        label="Baseline Measure Description"
                        value={measure.description || ''}
                        onChange={(e) => updateBaselineMeasure(index, 'description', e.target.value)}
                        onFocus={() => handleFieldClick(`baseline_characteristics.measures[${index}].description`)}
                        fullWidth
                        multiline
                        rows={3}
                        sx={{
                          '& .MuiInputBase-root': {
                            cursor: hasFieldSource(`baseline_characteristics.measures[${index}].description`) ? 'pointer' : 'text',
                          }
                        }}
                        inputProps={{ maxLength: 600 }}
                        helperText={
                          hasFieldSource(`baseline_characteristics.measures[${index}].description`)
                            ? `📄 Focus to view source on page ${getFieldMetadata(`baseline_characteristics.measures[${index}].description`)?.page} | ${(measure.description || '').length}/600 characters. Additional descriptive information about the baseline measure, such as a description of the metric used to characterize the specific baseline measure.`
                            : `${(measure.description || '').length}/600 characters. Additional descriptive information about the baseline measure, such as a description of the metric used to characterize the specific baseline measure.`
                        }
                        disabled={loading || saving}
                      />
                    </Grid>
                  )}

                  {/* Measure Type - only show for Age (Continuous/Customized) and Study-Specific Measure */}
                  {((measure.measure_title === 'Age' && ['Age, Continuous', 'Age, Customized'].includes(measure.measure_sub_type)) || 
                    measure.measure_title === 'Study-Specific Measure') && (
                    <Grid item xs={12} md={6}>
                    <TextField
                      select
                      label="Measure Type *"
                      value={measure.measure_type || ''}
                      onChange={(e) => updateBaselineMeasure(index, 'measure_type', e.target.value)}
                      fullWidth
                      disabled={loading || saving}
                      required
                      helperText="The type of data for the baseline measure. Select one."
                    >
                      {measure.measure_title === 'Age' && measure.measure_sub_type === 'Age, Categorical' ? (
                        // For Age Categorical, primarily Count of Participants
                        ['Count of Participants', 'Number'].map((opt) => (
                          <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                        ))
                      ) : measure.measure_title === 'Age' && measure.measure_sub_type === 'Age, Continuous' ? (
                        // For Age Continuous, statistical measures (no Count options)
                        ['Mean', 'Median', 'Least Squares Mean', 'Geometric Mean', 'Geometric Least Squares Mean'].map((opt) => (
                          <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                        ))
                      ) : (
                        // For Age Customized and other measures, all options
                        ['Count of Participants','Mean','Median','Least Squares Mean','Geometric Mean','Geometric Least Squares Mean','Number','Count of Units'].map((opt) => (
                          <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                        ))
                      )}
                    </TextField>
                    </Grid>
                  )}
                  
                  {/* Measure of Dispersion - only show for Age (Continuous/Customized) and Study-Specific Measure */}
                  {((measure.measure_title === 'Age' && ['Age, Continuous', 'Age, Customized'].includes(measure.measure_sub_type)) || 
                    measure.measure_title === 'Study-Specific Measure') && (
                    <Grid item xs={12} md={6}>
                    <TextField
                      select
                      label="Measure of Dispersion *"
                      value={measure.dispersion_type || ''}
                      onChange={(e) => updateBaselineMeasure(index, 'dispersion_type', e.target.value)}
                      fullWidth
                      disabled={loading || saving || (measure.measure_type && ['Number', 'Count of Participants', 'Count of Units'].includes(measure.measure_type) && measure.dispersion_type === 'Not Applicable')}
                      required
                      helperText={measure.measure_type && ['Number', 'Count of Participants', 'Count of Units'].includes(measure.measure_type) 
                        ? 'Not Applicable (only if Measure Type is "Number", "Count of Participants", or "Count of Units")'
                        : 'Select one.'}
                    >
                      {measure.measure_type && ['Number', 'Count of Participants', 'Count of Units'].includes(measure.measure_type) ? (
                        // If Measure Type is Number/Count, only Not Applicable
                        <MenuItem value="Not Applicable">Not Applicable</MenuItem>
                      ) : measure.measure_title === 'Age' && measure.measure_sub_type === 'Age, Continuous' ? (
                        // For Age Continuous, dispersion is required (no Not Applicable)
                        ['Standard Deviation', 'Inter-Quartile Range', 'Full Range'].map((opt) => (
                          <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                        ))
                      ) : (
                        // For others, all options
                        ['Not Applicable','Standard Deviation','Inter-Quartile Range','Full Range'].map((opt) => (
                          <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                        ))
                      )}
                    </TextField>
                    </Grid>
                  )}

                  {/* Optional fields: Number of Baseline Participants, Number of Units Analyzed, Analysis Population Type, Measure Analysis Population Description */}
                  {/* These fields are NOT shown in the standard templates based on the images */}
                  
                  {/* Unit of Measure - shown for all measure types */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Unit of Measure *"
                      value={measure.unit_of_measure || (
                        (measure.measure_title === 'Age' && measure.measure_sub_type === 'Age, Categorical') ||
                        measure.measure_title === 'Sex/Gender' ||
                        measure.measure_title === 'Race and Ethnicity' ||
                        measure.measure_title === 'Region of Enrollment'
                        ? 'Participants' : ''
                      )}
                      onChange={(e) => updateBaselineMeasure(index, 'unit_of_measure', e.target.value)}
                      onFocus={() => handleFieldClick(`baseline_characteristics.measures[${index}].unit_of_measure`)}
                      fullWidth
                      sx={{
                        '& .MuiInputBase-root': {
                          cursor: hasFieldSource(`baseline_characteristics.measures[${index}].unit_of_measure`) ? 'pointer' : 'text',
                        }
                      }}
                      inputProps={{ maxLength: 40 }}
                      helperText={
                        hasFieldSource(`baseline_characteristics.measures[${index}].unit_of_measure`)
                          ? `📄 Focus to view source on page ${getFieldMetadata(`baseline_characteristics.measures[${index}].unit_of_measure`)?.page} | ${(measure.unit_of_measure || '').length}/40 characters. An explanation of what is quantified by the data (for example, participants, mm Hg), for each baseline measure.`
                          : `${(measure.unit_of_measure || '').length}/40 characters. An explanation of what is quantified by the data (for example, participants, mm Hg), for each baseline measure.`
                      }
                      disabled={loading || saving}
                      required
                      placeholder={measure.measure_title === 'Age' && measure.measure_sub_type === 'Age, Continuous' ? 'e.g., years' : 'Participants'}
                    />
                  </Grid>

                  {/* Rows/Categories Section */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 2, fontWeight: 600 }}>
                      Rows/Categories *
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {measure.measure_title === 'Age' && measure.measure_sub_type === 'Age, Categorical' 
                        ? 'Enter data for each age category below. The three categories are automatically created.'
                        : measure.measure_title === 'Age' && measure.measure_sub_type === 'Age, Continuous'
                        ? 'Enter continuous age data below (typically one row with mean/median).'
                        : measure.measure_title === 'Age' && measure.measure_sub_type === 'Age, Customized'
                        ? 'Add custom age category rows as needed. Add one row per custom category.'
                        : 'Add rows/categories for this baseline measure. Add one row per category or data point.'}
                    </Typography>
                    
                    {(measure.rows || []).map((row, rowIndex) => (
                      <Paper key={rowIndex} elevation={0} sx={{ p: 2, mb: 2, bgcolor: '#f9f9f9', border: '1px solid #e0e0e0' }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                          <Typography variant="body2" fontWeight={500}>
                            {measure.measure_title === 'Age' && measure.measure_sub_type === 'Age, Categorical'
                              ? row.category_or_row_title || `Category ${rowIndex + 1}`
                              : `Row/Category ${rowIndex + 1}`}
                          </Typography>
                          {/* Show delete button only for customizable measure types, and only if more than 1 row */}
                          {(() => {
                            const isPredefinedCategory = 
                              (measure.measure_title === 'Age' && measure.measure_sub_type === 'Age, Categorical') ||
                              (measure.measure_title === 'Sex/Gender' && measure.measure_sub_type === 'Sex: Female, Male') ||
                              (measure.measure_title === 'Race and Ethnicity' && ['Race (NIH/OMB)', 'Ethnicity (NIH/OMB)'].includes(measure.measure_sub_type));
                            
                            const isCustomizable = 
                              (measure.measure_title === 'Age' && measure.measure_sub_type === 'Age, Customized') ||
                              (measure.measure_title === 'Sex/Gender' && measure.measure_sub_type === 'Sex/Gender, Customized') ||
                              (measure.measure_title === 'Race and Ethnicity' && ['Race/Ethnicity, Customized', 'Race and Ethnicity Not Collected'].includes(measure.measure_sub_type)) ||
                              measure.measure_title === 'Region of Enrollment' ||
                              measure.measure_title === 'Study-Specific Measure';
                            
                            return !isPredefinedCategory && isCustomizable && (measure.rows?.length || 0) > 1;
                          })() && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => removeBaselineMeasureRow(index, rowIndex)}
                              disabled={loading || saving}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Stack>
                        
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={4}>
                            {/* Show dropdown for predefined categories */}
                            {measure.measure_title === 'Age' && measure.measure_sub_type === 'Age, Categorical' ? (
                              <TextField
                                select
                                label="Category or Row Title *"
                                value={row.category_or_row_title || ''}
                                onChange={(e) => updateBaselineMeasureRow(index, rowIndex, 'category_or_row_title', e.target.value)}
                                fullWidth
                                disabled={loading || saving}
                                required
                                helperText="Name of distinct category or row for a baseline measure."
                              >
                                {ageCategoricalOptions.map((opt) => (
                                  <MenuItem key={opt} value={opt}>
                                    {opt}
                                  </MenuItem>
                                ))}
                              </TextField>
                            ) : measure.measure_title === 'Sex/Gender' && measure.measure_sub_type === 'Sex: Female, Male' ? (
                              <TextField
                                select
                                label="Category or Row Title *"
                                value={row.category_or_row_title || ''}
                                onChange={(e) => updateBaselineMeasureRow(index, rowIndex, 'category_or_row_title', e.target.value)}
                                fullWidth
                                disabled={loading || saving}
                                required
                                helperText="Sex category."
                              >
                                {sexGenderFemaleMaleOptions.map((opt) => (
                                  <MenuItem key={opt} value={opt}>
                                    {opt}
                                  </MenuItem>
                                ))}
                              </TextField>
                            ) : measure.measure_title === 'Race and Ethnicity' && measure.measure_sub_type === 'Race (NIH/OMB)' ? (
                              <TextField
                                select
                                label="Category or Row Title *"
                                value={row.category_or_row_title || ''}
                                onChange={(e) => updateBaselineMeasureRow(index, rowIndex, 'category_or_row_title', e.target.value)}
                                fullWidth
                                disabled={loading || saving}
                                required
                                helperText="Race category (NIH/OMB classification)."
                              >
                                {raceNIHOMBOptions.map((opt) => (
                                  <MenuItem key={opt} value={opt}>
                                    {opt}
                                  </MenuItem>
                                ))}
                              </TextField>
                            ) : measure.measure_title === 'Race and Ethnicity' && measure.measure_sub_type === 'Ethnicity (NIH/OMB)' ? (
                              <TextField
                                select
                                label="Category or Row Title *"
                                value={row.category_or_row_title || ''}
                                onChange={(e) => updateBaselineMeasureRow(index, rowIndex, 'category_or_row_title', e.target.value)}
                                fullWidth
                                disabled={loading || saving}
                                required
                                helperText="Ethnicity category (NIH/OMB classification)."
                              >
                                {ethnicityNIHOMBOptions.map((opt) => (
                                  <MenuItem key={opt} value={opt}>
                                    {opt}
                                  </MenuItem>
                                ))}
                              </TextField>
                            ) : (
                              <TextField
                                label="Category or Row Title"
                                value={row.category_or_row_title || ''}
                                onChange={(e) => updateBaselineMeasureRow(index, rowIndex, 'category_or_row_title', e.target.value)}
                                onFocus={() => handleFieldClick(`baseline_characteristics.measures[${index}].rows[${rowIndex}].category_or_row_title`)}
                                fullWidth
                                sx={{
                                  '& .MuiInputBase-root': {
                                    cursor: hasFieldSource(`baseline_characteristics.measures[${index}].rows[${rowIndex}].category_or_row_title`) ? 'pointer' : 'text',
                                  }
                                }}
                                inputProps={{ maxLength: 50 }}
                                helperText={
                                  hasFieldSource(`baseline_characteristics.measures[${index}].rows[${rowIndex}].category_or_row_title`)
                                    ? `📄 Focus to view source on page ${getFieldMetadata(`baseline_characteristics.measures[${index}].rows[${rowIndex}].category_or_row_title`)?.page} | ${(row.category_or_row_title || '').length}/50 characters. Name of distinct category or row for a baseline measure, if any.`
                                    : `${(row.category_or_row_title || '').length}/50 characters. Name of distinct category or row for a baseline measure, if any.`
                                }
                                disabled={loading || saving}
                                placeholder="e.g., Female, White, United States"
                              />
                            )}
                          </Grid>
                          
                          {/* Data per arm/group */}
                          {(formData.baseline_characteristics?.arms_groups || []).map((arm, armIndex) => {
                            const armData = getBaselineRowDataForArm(index, rowIndex, armIndex);
                            return (
                              <React.Fragment key={armIndex}>
                                <Grid item xs={12}>
                                  <Divider sx={{ my: 1 }}>
                                    <Typography variant="caption" fontWeight={600}>
                                      {arm.title || `Arm/Group ${armIndex + 1}`}
                                    </Typography>
                                  </Divider>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                  <TextField
                                    label={`Baseline Measure Data * (${arm.title || `Arm ${armIndex + 1}`})`}
                                    value={armData?.baseline_measure_data || ''}
                                    onChange={(e) => updateBaselineRowDataForArm(index, rowIndex, armIndex, 'baseline_measure_data', e.target.value)}
                                    onFocus={() => handleFieldClick(`baseline_characteristics.measures[${index}].rows[${rowIndex}].data_per_arm[${armIndex}].baseline_measure_data`)}
                                    fullWidth
                                    required
                                    sx={{
                                      '& .MuiInputBase-root': {
                                        cursor: hasFieldSource(`baseline_characteristics.measures[${index}].rows[${rowIndex}].data_per_arm[${armIndex}].baseline_measure_data`) ? 'pointer' : 'text',
                                      }
                                    }}
                                    disabled={loading || saving}
                                    helperText={
                                      hasFieldSource(`baseline_characteristics.measures[${index}].rows[${rowIndex}].data_per_arm[${armIndex}].baseline_measure_data`)
                                        ? `📄 Focus to view source on page ${getFieldMetadata(`baseline_characteristics.measures[${index}].rows[${rowIndex}].data_per_arm[${armIndex}].baseline_measure_data`)?.page} | The value for this arm/group`
                                        : 'The value for this arm/group'
                                    }
                                    placeholder="Enter value for this arm/group"
                                  />
                                </Grid>
                              </React.Fragment>
                            );
                          })}
                          
                          {/* Total for this row */}
                          {(formData.baseline_characteristics.arms_groups || []).length > 0 && (
                            <>
                              <Grid item xs={12}>
                                <Divider sx={{ my: 1 }}>
                                  <Typography variant="caption" fontWeight={600} color="primary">
                                    Total (Sum of All Arms/Groups)
                                  </Typography>
                                </Divider>
                              </Grid>
                              <Grid item xs={12} md={4}>
                                <TextField
                                  label="Total: Baseline Measure Data"
                                  value={calculateBaselineMeasureDataTotal(index, rowIndex)}
                                  fullWidth
                                  disabled
                                  InputProps={{
                                    readOnly: true,
                                  }}
                                  helperText="Automatically calculated sum of all arms/groups for this row"
                                  sx={{
                                    '& .MuiInputBase-root': {
                                      backgroundColor: '#f5f5f5',
                                      fontWeight: 600,
                                    },
                                  }}
                                />
                              </Grid>
                            </>
                          )}
                          
                          <Grid item xs={12}>
                            <TextField
                              label="NA (Not Available) Explanation"
                              value={row.na_explanation || ''}
                              onChange={(e) => updateBaselineMeasureRow(index, rowIndex, 'na_explanation', e.target.value)}
                              onFocus={() => handleFieldClick(`baseline_characteristics.measures[${index}].rows[${rowIndex}].na_explanation`)}
                              fullWidth
                              multiline
                              rows={2}
                              sx={{
                                '& .MuiInputBase-root': {
                                  cursor: hasFieldSource(`baseline_characteristics.measures[${index}].rows[${rowIndex}].na_explanation`) ? 'pointer' : 'text',
                                }
                              }}
                              inputProps={{ maxLength: 500 }}
                              helperText={
                                hasFieldSource(`baseline_characteristics.measures[${index}].rows[${rowIndex}].na_explanation`)
                                  ? `📄 Focus to view source on page ${getFieldMetadata(`baseline_characteristics.measures[${index}].rows[${rowIndex}].na_explanation`)?.page} | ${(row.na_explanation || '').length}/500 characters. Explain why baseline measure data are not available, if "NA" is reported for Baseline Measure Data.`
                                  : `${(row.na_explanation || '').length}/500 characters. Explain why baseline measure data are not available, if "NA" is reported for Baseline Measure Data.`
                              }
                              disabled={loading || saving}
                              placeholder="Explain why data is not available if 'NA' is reported"
                            />
                          </Grid>
                        </Grid>
                      </Paper>
                    ))}
                    
                    {/* Show "Add Row" button only for customizable measure types */}
                    {(() => {
                      const isPredefinedCategory = 
                        (measure.measure_title === 'Age' && ['Age, Categorical', 'Age, Continuous'].includes(measure.measure_sub_type)) ||
                        (measure.measure_title === 'Sex/Gender' && measure.measure_sub_type === 'Sex: Female, Male') ||
                        (measure.measure_title === 'Race and Ethnicity' && ['Race (NIH/OMB)', 'Ethnicity (NIH/OMB)'].includes(measure.measure_sub_type));
                      
                      const isCustomizable = 
                        (measure.measure_title === 'Age' && measure.measure_sub_type === 'Age, Customized') ||
                        (measure.measure_title === 'Sex/Gender' && measure.measure_sub_type === 'Sex/Gender, Customized') ||
                        (measure.measure_title === 'Race and Ethnicity' && ['Race/Ethnicity, Customized', 'Race and Ethnicity Not Collected'].includes(measure.measure_sub_type)) ||
                        measure.measure_title === 'Region of Enrollment' ||
                        measure.measure_title === 'Study-Specific Measure';
                      
                      return !isPredefinedCategory && isCustomizable;
                    })() && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => addBaselineMeasureRow(index)}
                        disabled={loading || saving}
                        sx={{ mt: 1 }}
                      >
                        Add Row/Category
                      </Button>
                    )}
                    
                    {/* Helper text for predefined categories */}
                    {measure.measure_title === 'Age' && measure.measure_sub_type === 'Age, Categorical' && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        The three age categories are automatically created and cannot be modified.
                      </Typography>
                    )}
                    {measure.measure_title === 'Age' && measure.measure_sub_type === 'Age, Continuous' && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Enter continuous age data in the row above (typically mean or median age).
                      </Typography>
                    )}
                    {measure.measure_title === 'Sex/Gender' && measure.measure_sub_type === 'Sex: Female, Male' && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Female and Male categories are automatically created and cannot be modified.
                      </Typography>
                    )}
                    {measure.measure_title === 'Race and Ethnicity' && measure.measure_sub_type === 'Race (NIH/OMB)' && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Race categories (NIH/OMB classification) are automatically created and cannot be modified.
                      </Typography>
                    )}
                    {measure.measure_title === 'Race and Ethnicity' && measure.measure_sub_type === 'Ethnicity (NIH/OMB)' && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Ethnicity categories (NIH/OMB classification) are automatically created and cannot be modified.
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </Paper>
            ))}

            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={addBaselineMeasure}
              disabled={loading || saving}
              sx={{ mt: 2 }}
            >
              Add Baseline Measure
            </Button>
          </Box>
        </Box>

        {/* Outcome Measures Section */}
        <Divider sx={{ my: 4 }} />
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            Outcome Measures
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            A table of data for each primary and secondary outcome measure by arm or comparison group.
          </Typography>

          {(formData.outcome_measures || []).map((measure, index) => (
            <Paper key={index} elevation={2} sx={{ p: 3, mb: 3, border: '2px solid #e3f2fd' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                <Typography variant="h6">
                  Outcome Measure {index + 1}
                </Typography>
                {(formData.outcome_measures || []).length > 1 && (
                  <IconButton
                    color="error"
                    onClick={() => removeOutcomeMeasure(index)}
                    disabled={loading || saving}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Stack>

              <Grid container spacing={2}>
                {/* Outcome Measure Type */}
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    label="Outcome Measure Type *"
                    value={measure.outcome_measure_type || ''}
                    onChange={(e) => updateOutcomeMeasure(index, 'outcome_measure_type', e.target.value)}
                    fullWidth
                    required
                    disabled={loading || saving}
                  >
                    {outcomeMeasureTypeOptions.map((opt) => (
                      <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {/* Outcome Measure Title */}
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Outcome Measure Title *"
                    value={measure.outcome_measure_title || ''}
                    onChange={(e) => updateOutcomeMeasure(index, 'outcome_measure_title', e.target.value)}
                    onFocus={() => handleFieldClick(`outcome_measures[${index}].outcome_measure_title`)}
                    fullWidth
                    required
                    sx={{
                      '& .MuiInputBase-root': {
                        cursor: hasFieldSource(`outcome_measures[${index}].outcome_measure_title`) ? 'pointer' : 'text',
                      }
                    }}
                    inputProps={{ maxLength: 255 }}
                    helperText={
                      hasFieldSource(`outcome_measures[${index}].outcome_measure_title`)
                        ? `📄 Focus to view source on page ${getFieldMetadata(`outcome_measures[${index}].outcome_measure_title`)?.page} | ${(measure.outcome_measure_title || '').length}/255 characters`
                        : `${(measure.outcome_measure_title || '').length}/255 characters`
                    }
                    disabled={loading || saving}
                  />
                </Grid>

                {/* Outcome Measure Description */}
                <Grid item xs={12}>
                  <TextField
                    label="Outcome Measure Description"
                    value={measure.outcome_measure_description || ''}
                    onChange={(e) => updateOutcomeMeasure(index, 'outcome_measure_description', e.target.value)}
                    onFocus={() => handleFieldClick(`outcome_measures[${index}].outcome_measure_description`)}
                    fullWidth
                    multiline
                    rows={3}
                    sx={{
                      '& .MuiInputBase-root': {
                        cursor: hasFieldSource(`outcome_measures[${index}].outcome_measure_description`) ? 'pointer' : 'text',
                      }
                    }}
                    inputProps={{ maxLength: 999 }}
                    helperText={
                      hasFieldSource(`outcome_measures[${index}].outcome_measure_description`)
                        ? `📄 Focus to view source on page ${getFieldMetadata(`outcome_measures[${index}].outcome_measure_description`)?.page} | ${(measure.outcome_measure_description || '').length}/999 characters`
                        : `${(measure.outcome_measure_description || '').length}/999 characters`
                    }
                    disabled={loading || saving}
                  />
                </Grid>

                {/* Time Frame */}
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Outcome Measure Time Frame *"
                    value={measure.outcome_measure_time_frame || ''}
                    onChange={(e) => updateOutcomeMeasure(index, 'outcome_measure_time_frame', e.target.value)}
                    onFocus={() => handleFieldClick(`outcome_measures[${index}].outcome_measure_time_frame`)}
                    fullWidth
                    required
                    sx={{
                      '& .MuiInputBase-root': {
                        cursor: hasFieldSource(`outcome_measures[${index}].outcome_measure_time_frame`) ? 'pointer' : 'text',
                      }
                    }}
                    inputProps={{ maxLength: 255 }}
                    helperText={
                      hasFieldSource(`outcome_measures[${index}].outcome_measure_time_frame`)
                        ? `📄 Focus to view source on page ${getFieldMetadata(`outcome_measures[${index}].outcome_measure_time_frame`)?.page} | ${(measure.outcome_measure_time_frame || '').length}/255 characters`
                        : `${(measure.outcome_measure_time_frame || '').length}/255 characters`
                    }
                    disabled={loading || saving}
                  />
                </Grid>

                {/* Anticipated Reporting Date */}
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Anticipated Reporting Date"
                    value={measure.anticipated_reporting_date || ''}
                    onChange={(e) => updateOutcomeMeasure(index, 'anticipated_reporting_date', e.target.value)}
                    onFocus={() => handleFieldClick(`outcome_measures[${index}].anticipated_reporting_date`)}
                    fullWidth
                    sx={{
                      '& .MuiInputBase-root': {
                        cursor: hasFieldSource(`outcome_measures[${index}].anticipated_reporting_date`) ? 'pointer' : 'text',
                      }
                    }}
                    disabled={loading || saving}
                    placeholder="Expected month and year"
                    helperText={
                      hasFieldSource(`outcome_measures[${index}].anticipated_reporting_date`)
                        ? `📄 Focus to view source on page ${getFieldMetadata(`outcome_measures[${index}].anticipated_reporting_date`)?.page}`
                        : undefined
                    }
                  />
                </Grid>
              </Grid>

              {/* Arms/Groups */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                  Arms/Groups *
                </Typography>
                {(measure.arms_groups || []).map((arm, armIndex) => (
                  <Paper key={armIndex} elevation={0} sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                      <Typography variant="body2" fontWeight={500}>
                        Arm/Group {armIndex + 1}
                      </Typography>
                      {(measure.arms_groups || []).length > 1 && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeOutcomeMeasureArmGroup(index, armIndex)}
                          disabled={loading || saving}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Stack>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Arm/Group Title *"
                          value={arm.title || ''}
                          onChange={(e) => updateOutcomeMeasureArmGroup(index, armIndex, 'title', e.target.value)}
                          onFocus={() => handleFieldClick(`outcome_measures[${index}].arms_groups[${armIndex}].title`)}
                          fullWidth
                          required
                          sx={{
                            '& .MuiInputBase-root': {
                              cursor: hasFieldSource(`outcome_measures[${index}].arms_groups[${armIndex}].title`) ? 'pointer' : 'text',
                            }
                          }}
                          inputProps={{ minLength: 4, maxLength: 100 }}
                          disabled={loading || saving}
                          helperText={
                            hasFieldSource(`outcome_measures[${index}].arms_groups[${armIndex}].title`)
                              ? `📄 Focus to view source on page ${getFieldMetadata(`outcome_measures[${index}].arms_groups[${armIndex}].title`)?.page} | ${(arm.title || '').length} characters (4-100 required)`
                              : `${(arm.title || '').length} characters (4-100 required)`
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Arm/Group Description *"
                          value={arm.description || ''}
                          onChange={(e) => updateOutcomeMeasureArmGroup(index, armIndex, 'description', e.target.value)}
                          onFocus={() => handleFieldClick(`outcome_measures[${index}].arms_groups[${armIndex}].description`)}
                          fullWidth
                          required
                          multiline
                          rows={2}
                          sx={{
                            '& .MuiInputBase-root': {
                              cursor: hasFieldSource(`outcome_measures[${index}].arms_groups[${armIndex}].description`) ? 'pointer' : 'text',
                            }
                          }}
                          inputProps={{ maxLength: 1500 }}
                          disabled={loading || saving}
                          helperText={
                            hasFieldSource(`outcome_measures[${index}].arms_groups[${armIndex}].description`)
                              ? `📄 Focus to view source on page ${getFieldMetadata(`outcome_measures[${index}].arms_groups[${armIndex}].description`)?.page} | ${(arm.description || '').length}/1500 characters`
                              : `${(arm.description || '').length}/1500 characters`
                          }
                        />
                      </Grid>
                      {/* Analysis Population per arm/group */}
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Overall Number of Participants Analyzed *"
                          value={arm.overall_participants_analyzed || ''}
                          onChange={(e) => updateOutcomeMeasureArmGroup(index, armIndex, 'overall_participants_analyzed', e.target.value)}
                          onFocus={() => handleFieldClick(`outcome_measures[${index}].arms_groups[${armIndex}].overall_participants_analyzed`)}
                          fullWidth
                          required
                          sx={{
                            '& .MuiInputBase-root': {
                              cursor: hasFieldSource(`outcome_measures[${index}].arms_groups[${armIndex}].overall_participants_analyzed`) ? 'pointer' : 'text',
                            }
                          }}
                          disabled={loading || saving}
                          helperText={
                            hasFieldSource(`outcome_measures[${index}].arms_groups[${armIndex}].overall_participants_analyzed`)
                              ? `📄 Focus to view source on page ${getFieldMetadata(`outcome_measures[${index}].arms_groups[${armIndex}].overall_participants_analyzed`)?.page} | Number of participants analyzed for this arm/group`
                              : 'Number of participants analyzed for this arm/group'
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Overall Number of Units Analyzed"
                          value={arm.overall_units_analyzed || ''}
                          onChange={(e) => updateOutcomeMeasureArmGroup(index, armIndex, 'overall_units_analyzed', e.target.value)}
                          onFocus={() => handleFieldClick(`outcome_measures[${index}].arms_groups[${armIndex}].overall_units_analyzed`)}
                          fullWidth
                          sx={{
                            '& .MuiInputBase-root': {
                              cursor: hasFieldSource(`outcome_measures[${index}].arms_groups[${armIndex}].overall_units_analyzed`) ? 'pointer' : 'text',
                            }
                          }}
                          disabled={loading || saving}
                          helperText={
                            hasFieldSource(`outcome_measures[${index}].arms_groups[${armIndex}].overall_units_analyzed`)
                              ? `📄 Focus to view source on page ${getFieldMetadata(`outcome_measures[${index}].arms_groups[${armIndex}].overall_units_analyzed`)?.page} | If analysis is based on units other than participants`
                              : 'If analysis is based on units other than participants'
                          }
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => addOutcomeMeasureArmGroup(index)}
                  disabled={loading || saving}
                >
                  Add Arm/Group
                </Button>
              </Box>

              {/* Shared Analysis Population Information */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                  Shared Analysis Population Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Type of Units Analyzed"
                      value={measure.analysis_population?.type_of_units_analyzed || ''}
                      onChange={(e) => updateOutcomeMeasure(index, 'analysis_population', { ...(measure.analysis_population || {}), type_of_units_analyzed: e.target.value })}
                      fullWidth
                      inputProps={{ maxLength: 40 }}
                      helperText="Shared description of unit type (e.g., eyes, lesions, implants)"
                      disabled={loading || saving}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Analysis Population Description"
                      value={measure.analysis_population?.analysis_population_description || ''}
                      onChange={(e) => updateOutcomeMeasure(index, 'analysis_population', { ...(measure.analysis_population || {}), analysis_population_description: e.target.value })}
                      onFocus={() => handleFieldClick(`outcome_measures[${index}].analysis_population.analysis_population_description`)}
                      fullWidth
                      multiline
                      rows={2}
                      sx={{
                        '& .MuiInputBase-root': {
                          cursor: hasFieldSource(`outcome_measures[${index}].analysis_population.analysis_population_description`) ? 'pointer' : 'text',
                        }
                      }}
                      inputProps={{ maxLength: 500 }}
                      helperText={
                        hasFieldSource(`outcome_measures[${index}].analysis_population.analysis_population_description`)
                          ? `📄 Focus to view source on page ${getFieldMetadata(`outcome_measures[${index}].analysis_population.analysis_population_description`)?.page} | Shared description of how the analysis population was determined`
                          : 'Shared description of how the analysis population was determined'
                      }
                      disabled={loading || saving}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Measure Data Table */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                  Outcome Measure Data Table
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      select
                      label="Measure Type *"
                      value={measure.measure_type || ''}
                      onChange={(e) => updateOutcomeMeasure(index, 'measure_type', e.target.value)}
                      fullWidth
                      required
                      disabled={loading || saving}
                    >
                      {measureTypeOptions.map((opt) => (
                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      select
                      label="Measure of Dispersion/Precision *"
                      value={measure.dispersion_precision || ''}
                      onChange={(e) => updateOutcomeMeasure(index, 'dispersion_precision', e.target.value)}
                      fullWidth
                      required
                      disabled={loading || saving}
                    >
                      {dispersionPrecisionOptions.map((opt) => (
                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Unit of Measure *"
                      value={measure.unit_of_measure || ''}
                      onChange={(e) => updateOutcomeMeasure(index, 'unit_of_measure', e.target.value)}
                      onFocus={() => handleFieldClick(`outcome_measures[${index}].unit_of_measure`)}
                      fullWidth
                      required
                      sx={{
                        '& .MuiInputBase-root': {
                          cursor: hasFieldSource(`outcome_measures[${index}].unit_of_measure`) ? 'pointer' : 'text',
                        }
                      }}
                      inputProps={{ maxLength: 40 }}
                      disabled={loading || saving}
                      helperText={
                        hasFieldSource(`outcome_measures[${index}].unit_of_measure`)
                          ? `📄 Focus to view source on page ${getFieldMetadata(`outcome_measures[${index}].unit_of_measure`)?.page} | ${(measure.unit_of_measure || '').length}/40 characters`
                          : `${(measure.unit_of_measure || '').length}/40 characters`
                      }
                    />
                  </Grid>
                  {measure.dispersion_precision === 'Other Confidence Interval Level' && (
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Other Confidence Interval Level"
                        value={measure.other_confidence_interval_level || ''}
                        onChange={(e) => updateOutcomeMeasure(index, 'other_confidence_interval_level', e.target.value)}
                        onFocus={() => handleFieldClick(`outcome_measures[${index}].other_confidence_interval_level`)}
                        fullWidth
                        sx={{
                          '& .MuiInputBase-root': {
                            cursor: hasFieldSource(`outcome_measures[${index}].other_confidence_interval_level`) ? 'pointer' : 'text',
                          }
                        }}
                        disabled={loading || saving}
                        helperText={
                          hasFieldSource(`outcome_measures[${index}].other_confidence_interval_level`)
                            ? `📄 Focus to view source on page ${getFieldMetadata(`outcome_measures[${index}].other_confidence_interval_level`)?.page}`
                            : undefined
                        }
                      />
                    </Grid>
                  )}
                </Grid>

                {/* Rows/Categories */}
                <Typography variant="subtitle2" sx={{ mt: 3, mb: 2, fontWeight: 600 }}>
                  Rows/Categories
                </Typography>
                {(measure.rows || []).map((row, rowIndex) => (
                  <Paper key={rowIndex} elevation={0} sx={{ p: 2, mb: 2, bgcolor: '#f9f9f9', border: '1px solid #e0e0e0' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                      <Typography variant="body2" fontWeight={500}>
                        Row {rowIndex + 1}
                      </Typography>
                      {(measure.rows || []).length > 1 && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeOutcomeMeasureRow(index, rowIndex)}
                          disabled={loading || saving}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Stack>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          label="Category or Row Title"
                          value={row.category_or_row_title || ''}
                          onChange={(e) => updateOutcomeMeasureRow(index, rowIndex, 'category_or_row_title', e.target.value)}
                          onFocus={() => handleFieldClick(`outcome_measures[${index}].rows[${rowIndex}].category_or_row_title`)}
                          fullWidth
                          sx={{
                            '& .MuiInputBase-root': {
                              cursor: hasFieldSource(`outcome_measures[${index}].rows[${rowIndex}].category_or_row_title`) ? 'pointer' : 'text',
                            }
                          }}
                          inputProps={{ maxLength: 100 }}
                          disabled={loading || saving}
                          helperText={
                            hasFieldSource(`outcome_measures[${index}].rows[${rowIndex}].category_or_row_title`)
                              ? `📄 Focus to view source on page ${getFieldMetadata(`outcome_measures[${index}].rows[${rowIndex}].category_or_row_title`)?.page} | ${(row.category_or_row_title || '').length}/100 characters`
                              : `${(row.category_or_row_title || '').length}/100 characters`
                          }
                        />
                      </Grid>
                      
                      {/* Data per arm/group */}
                      {(measure.arms_groups || []).map((arm, armIndex) => {
                        const armData = getOutcomeRowDataForArm(index, rowIndex, armIndex);
                        return (
                          <React.Fragment key={armIndex}>
                            <Grid item xs={12}>
                              <Divider sx={{ my: 1 }}>
                                <Typography variant="caption" fontWeight={600}>
                                  {arm.title || `Arm/Group ${armIndex + 1}`}
                                </Typography>
                              </Divider>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <TextField
                                label={`Outcome Measure Data * (${arm.title || `Arm ${armIndex + 1}`})`}
                                value={armData?.outcome_measure_data || ''}
                                onChange={(e) => updateOutcomeRowDataForArm(index, rowIndex, armIndex, 'outcome_measure_data', e.target.value)}
                                onFocus={() => handleFieldClick(`outcome_measures[${index}].rows[${rowIndex}].data_per_arm[${armIndex}].outcome_measure_data`)}
                                fullWidth
                                required
                                sx={{
                                  '& .MuiInputBase-root': {
                                    cursor: hasFieldSource(`outcome_measures[${index}].rows[${rowIndex}].data_per_arm[${armIndex}].outcome_measure_data`) ? 'pointer' : 'text',
                                  }
                                }}
                                disabled={loading || saving}
                                helperText={
                                  hasFieldSource(`outcome_measures[${index}].rows[${rowIndex}].data_per_arm[${armIndex}].outcome_measure_data`)
                                    ? `📄 Focus to view source on page ${getFieldMetadata(`outcome_measures[${index}].rows[${rowIndex}].data_per_arm[${armIndex}].outcome_measure_data`)?.page}`
                                    : undefined
                                }
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <TextField
                                label={`Number of Participants Analyzed (${arm.title || `Arm ${armIndex + 1}`})`}
                                value={armData?.number_of_participants_analyzed || ''}
                                onChange={(e) => updateOutcomeRowDataForArm(index, rowIndex, armIndex, 'number_of_participants_analyzed', e.target.value)}
                                onFocus={() => handleFieldClick(`outcome_measures[${index}].rows[${rowIndex}].data_per_arm[${armIndex}].number_of_participants_analyzed`)}
                                fullWidth
                                sx={{
                                  '& .MuiInputBase-root': {
                                    cursor: hasFieldSource(`outcome_measures[${index}].rows[${rowIndex}].data_per_arm[${armIndex}].number_of_participants_analyzed`) ? 'pointer' : 'text',
                                  }
                                }}
                                inputProps={{ maxLength: 50 }}
                                disabled={loading || saving}
                                helperText={
                                  hasFieldSource(`outcome_measures[${index}].rows[${rowIndex}].data_per_arm[${armIndex}].number_of_participants_analyzed`)
                                    ? `📄 Focus to view source on page ${getFieldMetadata(`outcome_measures[${index}].rows[${rowIndex}].data_per_arm[${armIndex}].number_of_participants_analyzed`)?.page} | If different from overall number`
                                    : 'If different from overall number'
                                }
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <TextField
                                label={`Number of Units Analyzed (${arm.title || `Arm ${armIndex + 1}`})`}
                                value={armData?.number_of_units_analyzed || ''}
                                onChange={(e) => updateOutcomeRowDataForArm(index, rowIndex, armIndex, 'number_of_units_analyzed', e.target.value)}
                                onFocus={() => handleFieldClick(`outcome_measures[${index}].rows[${rowIndex}].data_per_arm[${armIndex}].number_of_units_analyzed`)}
                                fullWidth
                                sx={{
                                  '& .MuiInputBase-root': {
                                    cursor: hasFieldSource(`outcome_measures[${index}].rows[${rowIndex}].data_per_arm[${armIndex}].number_of_units_analyzed`) ? 'pointer' : 'text',
                                  }
                                }}
                                disabled={loading || saving}
                                helperText={
                                  hasFieldSource(`outcome_measures[${index}].rows[${rowIndex}].data_per_arm[${armIndex}].number_of_units_analyzed`)
                                    ? `📄 Focus to view source on page ${getFieldMetadata(`outcome_measures[${index}].rows[${rowIndex}].data_per_arm[${armIndex}].number_of_units_analyzed`)?.page} | If different from overall number`
                                    : 'If different from overall number'
                                }
                              />
                            </Grid>
                          </React.Fragment>
                        );
                      })}
                      
                      <Grid item xs={12}>
                        <TextField
                          label="NA (Not Available) Explanation"
                          value={row.na_explanation || ''}
                          onChange={(e) => updateOutcomeMeasureRow(index, rowIndex, 'na_explanation', e.target.value)}
                          onFocus={() => handleFieldClick(`outcome_measures[${index}].rows[${rowIndex}].na_explanation`)}
                          fullWidth
                          multiline
                          rows={2}
                          sx={{
                            '& .MuiInputBase-root': {
                              cursor: hasFieldSource(`outcome_measures[${index}].rows[${rowIndex}].na_explanation`) ? 'pointer' : 'text',
                            }
                          }}
                          inputProps={{ maxLength: 500 }}
                          disabled={loading || saving}
                          helperText={
                            hasFieldSource(`outcome_measures[${index}].rows[${rowIndex}].na_explanation`)
                              ? `📄 Focus to view source on page ${getFieldMetadata(`outcome_measures[${index}].rows[${rowIndex}].na_explanation`)?.page} | Shared explanation if data is not available`
                              : 'Shared explanation if data is not available'
                          }
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => addOutcomeMeasureRow(index)}
                  disabled={loading || saving}
                  sx={{ mt: 1 }}
                >
                  Add Row/Category
                </Button>
              </Box>

              {/* Statistical Analyses */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                  Statistical Analyses
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Result(s) of scientifically appropriate tests of statistical significance (optional).
                </Typography>
                {(measure.statistical_analyses || []).map((analysis, analysisIndex) => (
                  <Paper key={analysisIndex} elevation={0} sx={{ p: 2, mb: 2, bgcolor: '#fafafa', border: '1px solid #e0e0e0' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                      <Typography variant="body2" fontWeight={500}>
                        Statistical Analysis {analysisIndex + 1}
                      </Typography>
                      {(measure.statistical_analyses || []).length > 1 && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeStatisticalAnalysis(index, analysisIndex)}
                          disabled={loading || saving}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Stack>

                    <Grid container spacing={2}>
                      {/* Statistical Analysis Overview */}
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                          Statistical Analysis Overview
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Type of Statistical Test"
                          select
                          value={analysis.type_of_statistical_test || ''}
                          onChange={(e) => updateStatisticalAnalysis(index, analysisIndex, 'type_of_statistical_test', e.target.value)}
                          fullWidth
                          disabled={loading || saving}
                        >
                          {statisticalTestTypeOptions.map((opt) => (
                            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Comments"
                          value={analysis.comments_overview || ''}
                          onChange={(e) => updateStatisticalAnalysis(index, analysisIndex, 'comments_overview', e.target.value)}
                          fullWidth
                          multiline
                          rows={2}
                          inputProps={{ maxLength: 500 }}
                          disabled={loading || saving}
                        />
                      </Grid>

                      {/* Statistical Test of Hypothesis */}
                      <Grid item xs={12}>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                          Statistical Test of Hypothesis
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="P-Value"
                          value={analysis.p_value || ''}
                          onChange={(e) => updateStatisticalAnalysis(index, analysisIndex, 'p_value', e.target.value)}
                          fullWidth
                          disabled={loading || saving}
                          helperText="Calculated p-value given the null-hypothesis"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Method"
                          select
                          value={analysis.method || ''}
                          onChange={(e) => updateStatisticalAnalysis(index, analysisIndex, 'method', e.target.value)}
                          fullWidth
                          disabled={loading || saving}
                        >
                          {statisticalMethodOptions.map((opt) => (
                            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      {analysis.method === 'Other' && (
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Other Method Name"
                            value={analysis.other_method_name || ''}
                            onChange={(e) => updateStatisticalAnalysis(index, analysisIndex, 'other_method_name', e.target.value)}
                            fullWidth
                            inputProps={{ maxLength: 40 }}
                            disabled={loading || saving}
                          />
                        </Grid>
                      )}
                      <Grid item xs={12}>
                        <TextField
                          label="P-Value Comments"
                          value={analysis.p_value_comments || ''}
                          onChange={(e) => updateStatisticalAnalysis(index, analysisIndex, 'p_value_comments', e.target.value)}
                          fullWidth
                          multiline
                          rows={2}
                          inputProps={{ maxLength: 250 }}
                          disabled={loading || saving}
                        />
                      </Grid>

                      {/* Method of Estimation */}
                      <Grid item xs={12}>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                          Method of Estimation
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Estimation Parameter"
                          select
                          value={analysis.estimation_parameter || ''}
                          onChange={(e) => updateStatisticalAnalysis(index, analysisIndex, 'estimation_parameter', e.target.value)}
                          fullWidth
                          disabled={loading || saving}
                        >
                          {estimationParameterOptions.map((opt) => (
                            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      {analysis.estimation_parameter === 'Other' && (
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Other Parameter Name"
                            value={analysis.other_parameter_name || ''}
                            onChange={(e) => updateStatisticalAnalysis(index, analysisIndex, 'other_parameter_name', e.target.value)}
                            fullWidth
                            inputProps={{ maxLength: 40 }}
                            disabled={loading || saving}
                          />
                        </Grid>
                      )}
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Estimated Value"
                          value={analysis.estimated_value || ''}
                          onChange={(e) => updateStatisticalAnalysis(index, analysisIndex, 'estimated_value', e.target.value)}
                          fullWidth
                          disabled={loading || saving}
                        />
                      </Grid>

                      {/* Confidence Interval */}
                      <Grid item xs={12}>
                        <Typography variant="body2" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
                          Confidence Interval
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          label="Level (%)"
                          value={analysis.confidence_interval?.level || ''}
                          onChange={(e) => updateStatisticalAnalysis(index, analysisIndex, 'confidence_interval', { ...(analysis.confidence_interval || {}), level: e.target.value })}
                          fullWidth
                          disabled={loading || saving}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          select
                          label="Number of Sides"
                          value={analysis.confidence_interval?.number_of_sides || ''}
                          onChange={(e) => updateStatisticalAnalysis(index, analysisIndex, 'confidence_interval', { ...(analysis.confidence_interval || {}), number_of_sides: e.target.value })}
                          fullWidth
                          disabled={loading || saving}
                        >
                          <MenuItem value="1-sided">1-sided</MenuItem>
                          <MenuItem value="2-sided">2-sided</MenuItem>
                        </TextField>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          label="Lower Limit"
                          value={analysis.confidence_interval?.lower_limit || ''}
                          onChange={(e) => updateStatisticalAnalysis(index, analysisIndex, 'confidence_interval', { ...(analysis.confidence_interval || {}), lower_limit: e.target.value })}
                          fullWidth
                          disabled={loading || saving}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          label="Upper Limit"
                          value={analysis.confidence_interval?.upper_limit || ''}
                          onChange={(e) => updateStatisticalAnalysis(index, analysisIndex, 'confidence_interval', { ...(analysis.confidence_interval || {}), upper_limit: e.target.value })}
                          fullWidth
                          disabled={loading || saving}
                        />
                      </Grid>

                      {/* Parameter Dispersion */}
                      <Grid item xs={12} md={6}>
                        <TextField
                          select
                          label="Parameter Dispersion Type"
                          value={analysis.parameter_dispersion_type || ''}
                          onChange={(e) => updateStatisticalAnalysis(index, analysisIndex, 'parameter_dispersion_type', e.target.value)}
                          fullWidth
                          disabled={loading || saving}
                        >
                          <MenuItem value="Standard Deviation">Standard Deviation</MenuItem>
                          <MenuItem value="Standard Error of the Mean">Standard Error of the Mean</MenuItem>
                        </TextField>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Dispersion Value"
                          value={analysis.dispersion_value || ''}
                          onChange={(e) => updateStatisticalAnalysis(index, analysisIndex, 'dispersion_value', e.target.value)}
                          fullWidth
                          disabled={loading || saving}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Estimation Comments"
                          value={analysis.estimation_comments || ''}
                          onChange={(e) => updateStatisticalAnalysis(index, analysisIndex, 'estimation_comments', e.target.value)}
                          fullWidth
                          multiline
                          rows={2}
                          inputProps={{ maxLength: 250 }}
                          disabled={loading || saving}
                        />
                      </Grid>

                      {/* Other Statistical Analysis */}
                      <Grid item xs={12}>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                          Other Statistical Analysis
                        </Typography>
                        <TextField
                          label="Description and Results"
                          value={analysis.other_statistical_analysis || ''}
                          onChange={(e) => updateStatisticalAnalysis(index, analysisIndex, 'other_statistical_analysis', e.target.value)}
                          fullWidth
                          multiline
                          rows={4}
                          disabled={loading || saving}
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => addStatisticalAnalysis(index)}
                  disabled={loading || saving}
                  sx={{ mt: 1 }}
                >
                  Add Statistical Analysis
                </Button>
              </Box>
            </Paper>
          ))}

          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addOutcomeMeasure}
            disabled={loading || saving}
            sx={{ mt: 2 }}
          >
            Add Outcome Measure
          </Button>
        </Box>

        {/* Adverse Events Section */}
        <Divider sx={{ my: 4 }} />
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            Adverse Event Information
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Information for completing three tables: All-Cause Mortality, Serious Adverse Events, and Other Adverse Events.
          </Typography>

          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            {/* Common Fields */}
            <Typography variant="h6" gutterBottom>
              Common Information
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Time Frame *"
                  value={formData.adverse_events?.time_frame || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, adverse_events: { ...(prev.adverse_events || {}), time_frame: e.target.value } }))}
                  onFocus={() => handleFieldClick('adverse_events.time_frame')}
                  fullWidth
                  required
                  multiline
                  rows={2}
                  sx={{
                    '& .MuiInputBase-root': {
                      cursor: hasFieldSource('adverse_events.time_frame') ? 'pointer' : 'text',
                    }
                  }}
                  inputProps={{ maxLength: 500 }}
                  helperText={
                    hasFieldSource('adverse_events.time_frame')
                      ? `📄 Focus to view source on page ${getFieldMetadata('adverse_events.time_frame')?.page} | ${(formData.adverse_events?.time_frame || '').length}/500 characters. The specific period of time over which adverse event data were collected.`
                      : `${(formData.adverse_events?.time_frame || '').length}/500 characters. The specific period of time over which adverse event data were collected.`
                  }
                  disabled={loading || saving}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Adverse Event Reporting Description"
                  value={formData.adverse_events?.adverse_event_reporting_description || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, adverse_events: { ...(prev.adverse_events || {}), adverse_event_reporting_description: e.target.value } }))}
                  onFocus={() => handleFieldClick('adverse_events.adverse_event_reporting_description')}
                  fullWidth
                  multiline
                  rows={3}
                  sx={{
                    '& .MuiInputBase-root': {
                      cursor: hasFieldSource('adverse_events.adverse_event_reporting_description') ? 'pointer' : 'text',
                    }
                  }}
                  inputProps={{ maxLength: 500 }}
                  helperText={
                    hasFieldSource('adverse_events.adverse_event_reporting_description')
                      ? `📄 Focus to view source on page ${getFieldMetadata('adverse_events.adverse_event_reporting_description')?.page} | ${(formData.adverse_events?.adverse_event_reporting_description || '').length}/500 characters`
                      : `${(formData.adverse_events?.adverse_event_reporting_description || '').length}/500 characters`
                  }
                  disabled={loading || saving}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Source Vocabulary Name for Table Default"
                  value={formData.adverse_events?.source_vocabulary_name_default || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, adverse_events: { ...(prev.adverse_events || {}), source_vocabulary_name_default: e.target.value } }))}
                  onFocus={() => handleFieldClick('adverse_events.source_vocabulary_name_default')}
                  fullWidth
                  sx={{
                    '& .MuiInputBase-root': {
                      cursor: hasFieldSource('adverse_events.source_vocabulary_name_default') ? 'pointer' : 'text',
                    }
                  }}
                  inputProps={{ maxLength: 20 }}
                  helperText={
                    hasFieldSource('adverse_events.source_vocabulary_name_default')
                      ? `📄 Focus to view source on page ${getFieldMetadata('adverse_events.source_vocabulary_name_default')?.page} | e.g., SNOMED CT, MedDRA 10.0`
                      : 'e.g., SNOMED CT, MedDRA 10.0'
                  }
                  disabled={loading || saving}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  select
                  label="Collection Approach for Table Default *"
                  value={formData.adverse_events?.collection_approach_default || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, adverse_events: { ...(prev.adverse_events || {}), collection_approach_default: e.target.value } }))}
                  fullWidth
                  required
                  disabled={loading || saving}
                >
                  {collectionApproachOptions.map((opt) => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>

            {/* Arms/Groups for Adverse Events */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                Arms/Groups *
              </Typography>
              {(formData.adverse_events?.arms_groups || []).map((arm, index) => (
                <Paper key={index} elevation={0} sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Typography variant="body2" fontWeight={500}>
                      Arm/Group {index + 1}
                    </Typography>
                    {(formData.adverse_events?.arms_groups || []).length > 1 && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeAdverseEventArmGroup(index)}
                        disabled={loading || saving}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Stack>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Arm/Group Title *"
                        value={arm.title || ''}
                        onChange={(e) => updateAdverseEventArmGroup(index, 'title', e.target.value)}
                        onFocus={() => handleFieldClick(`adverse_events.arms_groups[${index}].title`)}
                        fullWidth
                        required
                        sx={{
                          '& .MuiInputBase-root': {
                            cursor: hasFieldSource(`adverse_events.arms_groups[${index}].title`) ? 'pointer' : 'text',
                          }
                        }}
                        inputProps={{ minLength: 4, maxLength: 100 }}
                        disabled={loading || saving}
                        helperText={
                          hasFieldSource(`adverse_events.arms_groups[${index}].title`)
                            ? `📄 Focus to view source on page ${getFieldMetadata(`adverse_events.arms_groups[${index}].title`)?.page} | ${(arm.title || '').length} characters (4-100 required)`
                            : `${(arm.title || '').length} characters (4-100 required)`
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Arm/Group Description *"
                        value={arm.description || ''}
                        onChange={(e) => updateAdverseEventArmGroup(index, 'description', e.target.value)}
                        onFocus={() => handleFieldClick(`adverse_events.arms_groups[${index}].description`)}
                        fullWidth
                        required
                        sx={{
                          '& .MuiInputBase-root': {
                            cursor: hasFieldSource(`adverse_events.arms_groups[${index}].description`) ? 'pointer' : 'text',
                          }
                        }}
                        multiline
                        rows={2}
                        inputProps={{ maxLength: 1500 }}
                        disabled={loading || saving}
                        helperText={
                          hasFieldSource(`adverse_events.arms_groups[${index}].description`)
                            ? `📄 Focus to view source on page ${getFieldMetadata(`adverse_events.arms_groups[${index}].description`)?.page} | ${(arm.description || '').length}/1500 characters`
                            : `${(arm.description || '').length}/1500 characters`
                        }
                      />
                    </Grid>
                  </Grid>
                </Paper>
              ))}
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={addAdverseEventArmGroup}
                disabled={loading || saving}
              >
                Add Arm/Group
              </Button>
            </Box>
          </Paper>

          {/* All-Cause Mortality */}
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              1. All-Cause Mortality
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              All anticipated and unanticipated deaths due to any cause.
            </Typography>

            {/* Data per arm/group */}
            {(formData.adverse_events?.arms_groups || []).map((arm, armIndex) => {
              const armData = getMortalityDataForArm('all_cause_mortality', armIndex);
              return (
                <Paper key={armIndex} elevation={0} sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5', border: '1px solid #e0e0e0' }}>
                  <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                    {arm.title || `Arm/Group ${armIndex + 1}`}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Total Number Affected *"
                        value={armData?.total_affected || ''}
                        onChange={(e) => updateMortalityDataForArm('all_cause_mortality', armIndex, 'total_affected', e.target.value)}
                        fullWidth
                        required
                        helperText="Number of participants who died due to any cause for this arm/group"
                        disabled={loading || saving}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Total Number at Risk *"
                        value={armData?.total_at_risk || ''}
                        onChange={(e) => updateMortalityDataForArm('all_cause_mortality', armIndex, 'total_at_risk', e.target.value)}
                        fullWidth
                        required
                        helperText="Number of participants included in the assessment for this arm/group"
                        disabled={loading || saving}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              );
            })}

            {/* Total Row */}
            {(formData.adverse_events?.arms_groups || []).length > 0 && (
              <Paper elevation={2} sx={{ p: 2, bgcolor: '#e3f2fd', border: '2px solid #1976d2' }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2, color: '#1976d2' }}>
                  Total (Sum of All Arms/Groups)
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Total: Number Affected"
                      value={calculateAdverseEventTotal('all_cause_mortality', 'total_affected')}
                      fullWidth
                      disabled
                      InputProps={{
                        readOnly: true,
                      }}
                      helperText="Automatically calculated sum of all arms/groups"
                      sx={{
                        '& .MuiInputBase-root': {
                          backgroundColor: '#ffffff',
                          fontWeight: 600,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Total: Number at Risk"
                      value={calculateAdverseEventTotal('all_cause_mortality', 'total_at_risk')}
                      fullWidth
                      disabled
                      InputProps={{
                        readOnly: true,
                      }}
                      helperText="Automatically calculated sum of all arms/groups"
                      sx={{
                        '& .MuiInputBase-root': {
                          backgroundColor: '#ffffff',
                          fontWeight: 600,
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            )}
          </Paper>

          {/* Serious Adverse Events */}
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              2. Serious Adverse Events
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              All anticipated and unanticipated serious adverse events, grouped by organ system.
            </Typography>

            {/* Totals per arm/group */}
            {(formData.adverse_events?.arms_groups || []).map((arm, armIndex) => {
              const armData = getMortalityDataForArm('serious_adverse_events', armIndex);
              return (
                <Paper key={armIndex} elevation={0} sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5', border: '1px solid #e0e0e0' }}>
                  <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                    {arm.title || `Arm/Group ${armIndex + 1}`} - Summary Totals
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Total Number Affected *"
                        value={armData?.total_affected || ''}
                        onChange={(e) => updateMortalityDataForArm('serious_adverse_events', armIndex, 'total_affected', e.target.value)}
                        fullWidth
                        required
                        helperText="Number of participants affected by one or more Serious Adverse Events for this arm/group"
                        disabled={loading || saving}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Total Number at Risk *"
                        value={armData?.total_at_risk || ''}
                        onChange={(e) => updateMortalityDataForArm('serious_adverse_events', armIndex, 'total_at_risk', e.target.value)}
                        fullWidth
                        required
                        helperText="Number of participants included in assessment for this arm/group"
                        disabled={loading || saving}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Total Number of Events *"
                        value={armData?.total_events || ''}
                        onChange={(e) => updateMortalityDataForArm('serious_adverse_events', armIndex, 'total_events', e.target.value)}
                        fullWidth
                        required
                        helperText="Total number of serious adverse events for this arm/group"
                        disabled={loading || saving}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              );
            })}

            {/* Total Row for Serious Adverse Events */}
            {(formData.adverse_events?.arms_groups || []).length > 0 && (
              <Paper elevation={2} sx={{ p: 2, mb: 3, bgcolor: '#e3f2fd', border: '2px solid #1976d2' }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2, color: '#1976d2' }}>
                  Total (Sum of All Arms/Groups)
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Total: Number Participants Affected"
                      value={calculateAdverseEventTotal('serious_adverse_events', 'total_affected')}
                      fullWidth
                      disabled
                      InputProps={{
                        readOnly: true,
                      }}
                      helperText="Automatically calculated sum of all arms/groups"
                      sx={{
                        '& .MuiInputBase-root': {
                          backgroundColor: '#ffffff',
                          fontWeight: 600,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Total: Number Participants at Risk"
                      value={calculateAdverseEventTotal('serious_adverse_events', 'total_at_risk')}
                      fullWidth
                      disabled
                      InputProps={{
                        readOnly: true,
                      }}
                      helperText="Automatically calculated sum of all arms/groups"
                      sx={{
                        '& .MuiInputBase-root': {
                          backgroundColor: '#ffffff',
                          fontWeight: 600,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Total: Number Events"
                      value={calculateAdverseEventTotal('serious_adverse_events', 'total_events')}
                      fullWidth
                      disabled
                      InputProps={{
                        readOnly: true,
                      }}
                      helperText="Automatically calculated sum of all arms/groups"
                      sx={{
                        '& .MuiInputBase-root': {
                          backgroundColor: '#ffffff',
                          fontWeight: 600,
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            )}

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom fontWeight={600}>
              Serious Adverse Event Terms
            </Typography>

            {(formData.adverse_events?.serious_adverse_events?.events || []).map((event, index) => (
              <Paper key={index} elevation={0} sx={{ p: 2, mb: 2, bgcolor: '#fafafa', border: '1px solid #e0e0e0' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight={500}>
                    Event {index + 1}
                  </Typography>
                  {(formData.adverse_events?.serious_adverse_events?.events || []).length > 1 && (
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removeSeriousAdverseEvent(index)}
                      disabled={loading || saving}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Stack>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Adverse Event Term *"
                      value={event.adverse_event_term || ''}
                      onChange={(e) => updateSeriousAdverseEvent(index, 'adverse_event_term', e.target.value)}
                      fullWidth
                      required
                      inputProps={{ maxLength: 100 }}
                      disabled={loading || saving}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      select
                      label="Organ System *"
                      value={event.organ_system || ''}
                      onChange={(e) => updateSeriousAdverseEvent(index, 'organ_system', e.target.value)}
                      fullWidth
                      required
                      disabled={loading || saving}
                    >
                      {organSystemOptions.map((opt) => (
                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Additional Description"
                      value={event.additional_description || ''}
                      onChange={(e) => updateSeriousAdverseEvent(index, 'additional_description', e.target.value)}
                      fullWidth
                      inputProps={{ maxLength: 250 }}
                      disabled={loading || saving}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Source Vocabulary Name"
                      value={event.source_vocabulary_name || ''}
                      onChange={(e) => updateSeriousAdverseEvent(index, 'source_vocabulary_name', e.target.value)}
                      fullWidth
                      inputProps={{ maxLength: 20 }}
                      helperText="Leave blank to use table default"
                      disabled={loading || saving}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      select
                      label="Collection Approach"
                      value={event.collection_approach || ''}
                      onChange={(e) => updateSeriousAdverseEvent(index, 'collection_approach', e.target.value)}
                      fullWidth
                      disabled={loading || saving}
                      helperText="Leave blank to use table default"
                    >
                      <MenuItem value="">Use Table Default</MenuItem>
                      {collectionApproachOptions.map((opt) => (
                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  
                  {/* Data per arm/group */}
                  {(formData.adverse_events?.arms_groups || []).map((arm, armIndex) => {
                    const armData = getSeriousEventDataForArm(index, armIndex);
                    return (
                      <React.Fragment key={armIndex}>
                        <Grid item xs={12}>
                          <Divider sx={{ my: 1 }}>
                            <Typography variant="caption" fontWeight={600}>
                              {arm.title || `Arm/Group ${armIndex + 1}`}
                            </Typography>
                          </Divider>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            label={`Number Affected * (${arm.title || `Arm ${armIndex + 1}`})`}
                            value={armData?.number_affected || ''}
                            onChange={(e) => updateSeriousEventDataForArm(index, armIndex, 'number_affected', e.target.value)}
                            fullWidth
                            required
                            disabled={loading || saving}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            label={`Number at Risk (${arm.title || `Arm ${armIndex + 1}`})`}
                            value={armData?.number_at_risk || ''}
                            onChange={(e) => updateSeriousEventDataForArm(index, armIndex, 'number_at_risk', e.target.value)}
                            fullWidth
                            disabled={loading || saving}
                            helperText="Only required if different from Total Number at Risk"
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            label={`Number of Events * (${arm.title || `Arm ${armIndex + 1}`})`}
                            value={armData?.number_of_events || ''}
                            onChange={(e) => updateSeriousEventDataForArm(index, armIndex, 'number_of_events', e.target.value)}
                            fullWidth
                            required
                            disabled={loading || saving}
                          />
                        </Grid>
                      </React.Fragment>
                    );
                  })}
                </Grid>
              </Paper>
            ))}

            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={addSeriousAdverseEvent}
              disabled={loading || saving}
              sx={{ mt: 1 }}
            >
              Add Serious Adverse Event
            </Button>
          </Paper>

          {/* Other (Not Including Serious) Adverse Events */}
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              3. Other (Not Including Serious) Adverse Events
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Anticipated and unanticipated events that exceed a frequency threshold.
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Frequency Threshold for Reporting (%) *"
                  value={formData.adverse_events?.other_adverse_events?.frequency_threshold || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, adverse_events: { ...prev.adverse_events, other_adverse_events: { ...(prev.adverse_events?.other_adverse_events || {}), frequency_threshold: e.target.value } } }))}
                  fullWidth
                  required
                  type="number"
                  helperText="e.g., 5 for 5% (no symbols)"
                  disabled={loading || saving}
                />
              </Grid>
            </Grid>

            {/* Totals per arm/group */}
            {(formData.adverse_events?.arms_groups || []).map((arm, armIndex) => {
              const armData = getMortalityDataForArm('other_adverse_events', armIndex);
              return (
                <Paper key={armIndex} elevation={0} sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5', border: '1px solid #e0e0e0' }}>
                  <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                    {arm.title || `Arm/Group ${armIndex + 1}`} - Summary Totals
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Total Number Affected *"
                        value={armData?.total_affected || ''}
                        onChange={(e) => updateMortalityDataForArm('other_adverse_events', armIndex, 'total_affected', e.target.value)}
                        fullWidth
                        required
                        helperText="Participants affected by at least one event above threshold for this arm/group"
                        disabled={loading || saving}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Total Number at Risk *"
                        value={armData?.total_at_risk || ''}
                        onChange={(e) => updateMortalityDataForArm('other_adverse_events', armIndex, 'total_at_risk', e.target.value)}
                        fullWidth
                        required
                        helperText="Number of participants included in assessment for this arm/group"
                        disabled={loading || saving}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Total Number of Events *"
                        value={armData?.total_events || ''}
                        onChange={(e) => updateMortalityDataForArm('other_adverse_events', armIndex, 'total_events', e.target.value)}
                        fullWidth
                        required
                        helperText="Total number of other adverse events for this arm/group"
                        disabled={loading || saving}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              );
            })}

            {/* Total Row for Other Adverse Events */}
            {(formData.adverse_events?.arms_groups || []).length > 0 && (
              <Paper elevation={2} sx={{ p: 2, mb: 3, bgcolor: '#e3f2fd', border: '2px solid #1976d2' }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2, color: '#1976d2' }}>
                  Total (Sum of All Arms/Groups)
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Total: Number Participants Affected"
                      value={calculateAdverseEventTotal('other_adverse_events', 'total_affected')}
                      fullWidth
                      disabled
                      InputProps={{
                        readOnly: true,
                      }}
                      helperText="Automatically calculated sum of all arms/groups"
                      sx={{
                        '& .MuiInputBase-root': {
                          backgroundColor: '#ffffff',
                          fontWeight: 600,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Total: Number Participants at Risk"
                      value={calculateAdverseEventTotal('other_adverse_events', 'total_at_risk')}
                      fullWidth
                      disabled
                      InputProps={{
                        readOnly: true,
                      }}
                      helperText="Automatically calculated sum of all arms/groups"
                      sx={{
                        '& .MuiInputBase-root': {
                          backgroundColor: '#ffffff',
                          fontWeight: 600,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Total: Number Events"
                      value={calculateAdverseEventTotal('other_adverse_events', 'total_events')}
                      fullWidth
                      disabled
                      InputProps={{
                        readOnly: true,
                      }}
                      helperText="Automatically calculated sum of all arms/groups"
                      sx={{
                        '& .MuiInputBase-root': {
                          backgroundColor: '#ffffff',
                          fontWeight: 600,
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            )}

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom fontWeight={600}>
              Other Adverse Event Terms
            </Typography>

            {(formData.adverse_events?.other_adverse_events?.events || []).map((event, index) => (
              <Paper key={index} elevation={0} sx={{ p: 2, mb: 2, bgcolor: '#fafafa', border: '1px solid #e0e0e0' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight={500}>
                    Event {index + 1}
                  </Typography>
                  {(formData.adverse_events?.other_adverse_events?.events || []).length > 1 && (
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removeOtherAdverseEvent(index)}
                      disabled={loading || saving}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Stack>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Adverse Event Term *"
                      value={event.adverse_event_term || ''}
                      onChange={(e) => updateOtherAdverseEvent(index, 'adverse_event_term', e.target.value)}
                      fullWidth
                      required
                      inputProps={{ maxLength: 100 }}
                      disabled={loading || saving}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      select
                      label="Organ System *"
                      value={event.organ_system || ''}
                      onChange={(e) => updateOtherAdverseEvent(index, 'organ_system', e.target.value)}
                      fullWidth
                      required
                      disabled={loading || saving}
                    >
                      {organSystemOptions.map((opt) => (
                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Additional Description"
                      value={event.additional_description || ''}
                      onChange={(e) => updateOtherAdverseEvent(index, 'additional_description', e.target.value)}
                      fullWidth
                      inputProps={{ maxLength: 250 }}
                      disabled={loading || saving}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Source Vocabulary Name"
                      value={event.source_vocabulary_name || ''}
                      onChange={(e) => updateOtherAdverseEvent(index, 'source_vocabulary_name', e.target.value)}
                      fullWidth
                      inputProps={{ maxLength: 20 }}
                      helperText="Leave blank to use table default"
                      disabled={loading || saving}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      select
                      label="Collection Approach"
                      value={event.collection_approach || ''}
                      onChange={(e) => updateOtherAdverseEvent(index, 'collection_approach', e.target.value)}
                      fullWidth
                      disabled={loading || saving}
                      helperText="Leave blank to use table default"
                    >
                      <MenuItem value="">Use Table Default</MenuItem>
                      {collectionApproachOptions.map((opt) => (
                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  
                  {/* Data per arm/group */}
                  {(formData.adverse_events?.arms_groups || []).map((arm, armIndex) => {
                    const armData = getOtherEventDataForArm(index, armIndex);
                    return (
                      <React.Fragment key={armIndex}>
                        <Grid item xs={12}>
                          <Divider sx={{ my: 1 }}>
                            <Typography variant="caption" fontWeight={600}>
                              {arm.title || `Arm/Group ${armIndex + 1}`}
                            </Typography>
                          </Divider>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            label={`Number Affected * (${arm.title || `Arm ${armIndex + 1}`})`}
                            value={armData?.number_affected || ''}
                            onChange={(e) => updateOtherEventDataForArm(index, armIndex, 'number_affected', e.target.value)}
                            fullWidth
                            required
                            disabled={loading || saving}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            label={`Number at Risk (${arm.title || `Arm ${armIndex + 1}`})`}
                            value={armData?.number_at_risk || ''}
                            onChange={(e) => updateOtherEventDataForArm(index, armIndex, 'number_at_risk', e.target.value)}
                            fullWidth
                            disabled={loading || saving}
                            helperText="Only required if different from Total Number at Risk"
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            label={`Number of Events (${arm.title || `Arm ${armIndex + 1}`})`}
                            value={armData?.number_of_events || ''}
                            onChange={(e) => updateOtherEventDataForArm(index, armIndex, 'number_of_events', e.target.value)}
                            fullWidth
                            disabled={loading || saving}
                          />
                        </Grid>
                      </React.Fragment>
                    );
                  })}
                </Grid>
              </Paper>
            ))}

            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={addOtherAdverseEvent}
              disabled={loading || saving}
              sx={{ mt: 1 }}
            >
              Add Other Adverse Event
            </Button>
          </Paper>
        </Box>

        {/* Save and Export Buttons */}
        <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 4 }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExportXml}
            disabled={loading || saving || exporting}
            size="large"
          >
            {exporting ? <CircularProgress size={20} /> : 'Export to XML'}
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading || saving || exporting}
            size="large"
          >
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </Stack>
          </Paper>
        </Grid>

        {/* Right Side - PDF Viewer */}
        <Grid item xs={12} md={6}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: 2, 
              height: '100%', 
              overflow: 'auto', 
              maxHeight: 'calc(100vh - 200px)',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              Uploaded Document
            </Typography>
            {fileInfo?.azureUrl ? (
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <PdfJsHighlightViewer 
                  ref={pdfViewerRef}
                  pdfId={`submit-results-${projectId}`}
                  pdfUrl={fileInfo.azureUrl}
                  programmaticHighlights={programmaticHighlights}
                />
              </Box>
            ) : (
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  height: '100%',
                  minHeight: 400,
                  border: '2px dashed #ccc',
                  borderRadius: 2,
                  backgroundColor: '#f5f5f5'
                }}
              >
                <Typography variant="body1" color="text.secondary">
                  Upload a document to view it here
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SubmitResultsPage;
